/*
 * CleanVINUSA - VIN visibility check widget
 * ------------------------------------------------------------------
 * Merge of the live (Debut) widget and the funnel rebuild:
 *   from live     : vehicle card + gallery, cleaned.vin partner link,
 *                   getApiData() response normalisation, handle
 *                   normalisation, Shopify.routes.root awareness
 *   from rebuild  : I/O/Q validation, DOM building (no innerHTML with
 *                   data), i18n, ranking + "recommended first step",
 *                   checkboxes with sticky total, ONE /cart/add.js call
 *   new           : deterministic ordering, request timeout, currency
 *                   via Intl, analytics events, per-site order status
 *                   (site_status / priority from the API - see part B),
 *                   accessible live region, mobile card layout
 *
 * ES5 ONLY. Shopify compiles theme assets to ES5; newer syntax is
 * served but never executed. Forbidden here:
 *   arrow functions, const/let, template literals, spread,
 *   .finally(), and .catch() as a property (use ['catch'](...)).
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------
   * Config
   * ------------------------------------------------------------- */

  var DEFAULTS = {
    apiUrl: 'https://api.cleanvinusa.com/check-vin',
    /* How big the pre-ticked set is.
     *
     * This used to be a flat `recMax: 5` — the same five rows whether the check
     * found six sites or thirty. At six found, five is nearly everything. At
     * twenty, five is a quarter, and the customer was being quoted a quarter of
     * their own problem while the only alternative was a 4x jump to "everything".
     * Order #20874 is the evidence: twelve sites found, five bought.
     *
     * So the set now scales with the find: ceil(found x share), held between a
     * floor and a cap. 12 found -> 8. 20 found -> 10 (the cap). 6 found -> 5
     * (the floor). The floor keeps small finds from shrinking below what the
     * page has always offered; the cap keeps a 40-site find from opening with a
     * number nobody reads as a starting point.
     *
     * `recMax` is kept only so an older stored setting cannot break the read;
     * nothing uses it. */
    recMax: 5,
    recShare: 60,
    recFloor: 5,
    recCap: 10,
    priority: [],
    siteNames: {},
    bundles: [],
    sla: {},
    slaDefault: '12-36h',
    routesRoot: '/',
    currency: 'USD',
    lang: 'en',
    showCompareAt: false,
    debug: false
  };

  var REQUEST_TIMEOUT_MS = 30000;
  var SLOW_NOTICE_MS = 8000;
  var REPORT_HANDLES = [
    'clean-vin-com-remove-report',
    'cleaned-vin-remove-report',
    'cleaned-vin-com-remove-report',
    'claim-vin-remove-report'
  ];

  var CFG = readConfig();

  function readConfig() {
    var cfg = {};
    var k;
    for (k in DEFAULTS) {
      if (Object.prototype.hasOwnProperty.call(DEFAULTS, k)) cfg[k] = DEFAULTS[k];
    }
    var node = document.getElementById('vin-check-config');
    if (node && node.textContent) {
      try {
        var raw = JSON.parse(node.textContent);
        for (k in raw) {
          if (Object.prototype.hasOwnProperty.call(raw, k) && raw[k] !== null && raw[k] !== '') {
            cfg[k] = raw[k];
          }
        }
      } catch (e) {
        warn('bad config JSON', e);
      }
    }
    // Same raw-string trick as siteNames: a typo in the setting can only break
    // the bundle map, never the whole config blob.
    if (typeof cfg.bundlesRaw === 'string' && cfg.bundlesRaw) {
      try { cfg.bundles = JSON.parse(cfg.bundlesRaw); }
      catch (e) { warn('bad bundles JSON in section settings', e); cfg.bundles = []; }
    }
    if (!isArray(cfg.bundles)) cfg.bundles = [];

    if (typeof cfg.slaRaw === 'string' && cfg.slaRaw) {
      try { cfg.sla = JSON.parse(cfg.slaRaw); }
      catch (e) { warn('bad delivery-time JSON in section settings', e); cfg.sla = {}; }
    }
    if (!cfg.sla || typeof cfg.sla !== 'object') cfg.sla = {};

    if (!isArray(cfg.priority)) cfg.priority = [];
    var cleaned = [];
    for (var i = 0; i < cfg.priority.length; i++) {
      var h = String(cfg.priority[i]).replace(/^\s+|\s+$/g, '');
      if (h) cleaned.push(h);
    }
    cfg.priority = cleaned;
    // The section passes the override map as a raw string, so a typo in the
    // setting can only break the map itself, never the whole config blob.
    if (typeof cfg.siteNamesRaw === 'string' && cfg.siteNamesRaw) {
      try { cfg.siteNames = JSON.parse(cfg.siteNamesRaw); }
      catch (e) { warn('bad siteNames JSON in section settings', e); cfg.siteNames = {}; }
    }
    if (!cfg.siteNames || typeof cfg.siteNames !== 'object') cfg.siteNames = {};
    return cfg;
  }

  /* ---------------------------------------------------------------
   * i18n - English is complete, others fall back to English per key.
   * Phase 3 moves these into locales/*.json and passes them in via
   * the section config, without touching this file.
   * ------------------------------------------------------------- */

  var STRINGS = {
    en: {
      add_site_open: 'Site not listed? Add it',
      add_site_title: 'Add a site from our catalogue',
      add_site_intro: 'Found your VIN somewhere the scan did not list? Pick it here and it goes in the same order.',
      add_site_search: 'Search by site name',
      add_site_none: 'Nothing matches that name.',
      add_site_covered: 'Every site we cover is already listed above or in your cart.',
      add_site_err: 'We could not load the site list. Please try again, or send us the address.',
      add_site_add: 'Add {n} · {price}',
      add_site_cancel: 'Cancel',
      add_site_close: 'Close',
      add_site_soon: 'Ask us about this site',
      add_site_added: 'Added {n} to your cart.',
      auto_cart_adding: 'Filling your cart with the {n} pages still showing your car…',
      auto_cart_none: 'Nothing left to do — every page from that e-mail is already handled.',
      add_site_go: 'Go to cart',
      checking: 'Checking…',
      err_empty: 'Please enter your VIN.',
      err_len: 'A VIN is exactly 17 characters. You entered {n}.',
      err_ioq: 'A VIN never contains the letters I, O or Q.',
      err_no_vin_found: 'No 17-character VIN in that. A Copart or IAAI lot link carries only a lot number — the VIN is not in it. Paste the 17 characters, or a link from a page that shows the VIN.',
      err_rate_limited: 'You have run this check a few times in a short period, so it is paused for a little while. Try again later, or email office@cleanvinusa.com with your VIN and we will run it by hand.',
      err_search_failed: 'We could not complete the check this time — so we cannot tell you whether your VIN is out there. Please try again in a moment or contact us and we will run it by hand.',
      found_at: 'Found at',
      more_pages: '+{n} more',
      page_removed: 'This page is gone',
      help_title: 'Not sure, or something looks wrong?',
      err_api: 'We could not complete the check right now.',
      err_cart: 'We could not add the items to your cart. Please try again.',
      retry: 'Try again',
      scanning: 'Scanning search results for your VIN…',
      scanning_slow: 'Still scanning — a full check can take up to a minute.',
      checked_at: 'Checked at {date}, {time}',
      checking_vin: 'Checking VIN',
      result_title: 'We found your VIN on {n} sites',
      result_title_one: 'We found your VIN on 1 site',
      full_cleanup: 'Full clean-up',
      recommended: 'Recommended first step',
      other_sites: 'Other sites where your VIN appears ({n})',
      already_handled: 'Already handled for this VIN',
      in_cart_group: 'Already in your cart',
      tag_in_cart: 'In your cart',
      none_title: 'We did not find your VIN on any of the sites we cover.',
      none_body: 'That is a good result. It does not mean the VIN is absent from every site on the internet — only that none of the sites we cover are showing it right now. If you expected to find something, send us a photo of the VIN plate and we will check it by hand.',
      selected: '{n} selected',
      selected_none: 'Nothing selected',
      add_to_cart: 'Add to cart',
      add_n_to_cart: 'Add {n} to cart · {price}',
      vehicle_intro: 'This is what anyone can see about your car by searching this VIN:',
      step_search: 'Searching Google and public listing sites',
      step_match: 'Matching what we find against the sites we cover',
      step_match_n: 'Matched {n} sites that publish your VIN',
      step_price: 'Checking prices and availability',
      eta_h: 'Usually done within {n}h',
      eta_h_range: 'Usually done in {a}–{b}h',
      eta_d: 'Usually done within {n} days',
      eta_d_range: 'Usually done in {a}–{b} days',
      n_sites: '{n} sites',
      choose_scope: 'Choose how much to remove',
      scope_core: 'Start with the main ones',
      scope_recommended: 'Recommended clean-up',
      scope_full: 'Remove everything',
      most_chosen: 'Most chosen',
      full_note: 'Covers the {n} pages you would otherwise leave online',
      leaves_note: 'Leaves {n} pages online',
      show_more: 'Show the other {n} · {price}',
      upsell_added: 'Added to cart — {n} pages, {price}',
      upsell_title: '{n} other pages still show this VIN',
      upsell_body: 'Found on the same VIN. They stay online unless they are removed too.',
      upsell_and_more: 'and {n} more',
      upsell_cost: 'That is {price} more, taking your cart to {total}.',
      upsell_add: 'Also remove the other {n} · {price}',
      upsell_skip: 'No thanks, go to cart',
      bundle_covers: 'One order covers {sites}',
      bundle_saving: 'Saves {amount}',
      err_not_a_vin: 'Please enter a real VIN.',
      partial_notice: 'Some search engines did not answer, so this list may be incomplete.',
      failed_partial: 'We could not finish the check, so this list may be missing sites. Contact us and we will run it by hand.',
      photo_n: 'Photo {n}',
      photo_prev: 'Previous photo',
      photo_next: 'Next photo',
      chip_vin: 'VIN',
      chip_lot: 'Lot',
      chip_auction: 'Auction',
      chip_location: 'Location',
      chip_sale_date: 'Sale date',
      chip_sold_for: 'Sold for',
      chip_damage: 'Damage',
      chip_odometer: 'Odometer',
      chip_doc: 'Title',
      unit_miles: 'mi',
      tag_serp: 'First page of Google',
      tag_photos: 'Auction photos',
      tag_fast: 'Fast removal',
      status_removed: 'Removed {date}',
      status_verified: 'Removed and verified {date}',
      status_progress: 'In progress',
      status_unavailable: 'Ask us about this site',
      unsupported: 'Your browser is too old for this tool. Please email office@cleanvinusa.com with your VIN and we will run the check for you.'
    },
    pl: {
      add_site_open: 'Nie ma Twojej strony? Dodaj ją',
      add_site_title: 'Dodaj stronę z naszego katalogu',
      add_site_intro: 'Znalazłeś swój VIN gdzieś, czego skan nie pokazał? Wybierz tę stronę tutaj — trafi do tego samego zamówienia.',
      add_site_search: 'Szukaj po nazwie strony',
      add_site_none: 'Nic nie pasuje do tej nazwy.',
      add_site_covered: 'Wszystkie strony, które obsługujemy, są już na liście powyżej albo w koszyku.',
      add_site_err: 'Nie udało się wczytać listy stron. Spróbuj ponownie albo prześlij nam adres.',
      add_site_add: 'Dodaj {n} · {price}',
      add_site_cancel: 'Anuluj',
      add_site_close: 'Zamknij',
      add_site_soon: 'Zapytaj nas o tę stronę',
      add_site_added: 'Dodano {n} do koszyka.',
      auto_cart_adding: 'Wkładam do koszyka {n} stron, na których wciąż widać Twój samochód…',
      auto_cart_none: 'Nie ma czego dodawać — wszystko z tamtego maila jest już załatwione.',
      add_site_go: 'Przejdź do koszyka',
      checking: 'Sprawdzam…',
      err_empty: 'Wpisz numer VIN.',
      err_len: 'VIN ma dokładnie 17 znaków. Wpisano {n}.',
      err_ioq: 'VIN nigdy nie zawiera liter I, O ani Q.',
      err_no_vin_found: 'Nie ma tu 17-znakowego VIN-u. Link do lotu na Copart czy IAAI zawiera tylko numer lotu — VIN-u w nim nie ma. Wklej 17 znaków albo link do strony, na której VIN jest widoczny.',
      err_rate_limited: 'To sprawdzenie było uruchamiane kilka razy w krótkim czasie, więc jest chwilowo wstrzymane. Spróbuj później albo napisz na office@cleanvinusa.com z numerem VIN — sprawdzimy ręcznie.',
      err_search_failed: 'Tym razem nie udało się dokończyć sprawdzenia — więc nie powiemy Ci, czy Twój VIN gdzieś jest. Spróbuj za chwilę albo napisz do nas, sprawdzimy ręcznie.',
      found_at: 'Znalezione pod adresem',
      more_pages: 'i jeszcze {n}',
      page_removed: 'Ta strona już nie istnieje',
      help_title: 'Nie masz pewności albo coś wygląda dziwnie?',
      err_api: 'Nie udało się teraz wykonać sprawdzenia.',
      err_cart: 'Nie udało się dodać pozycji do koszyka. Spróbuj ponownie.',
      retry: 'Spróbuj ponownie',
      scanning: 'Przeszukuję wyniki wyszukiwania dla Twojego VIN-u…',
      scanning_slow: 'Nadal szukam — pełne sprawdzenie może potrwać do minuty.',
      checked_at: 'Sprawdzono {date}, {time}',
      checking_vin: 'Sprawdzany VIN',
      result_title: 'Znaleźliśmy Twój VIN na {n} stronach',
      result_title_one: 'Znaleźliśmy Twój VIN na 1 stronie',
      full_cleanup: 'Pełne wyczyszczenie',
      recommended: 'Rekomendowany pierwszy krok',
      other_sites: 'Pozostałe strony z Twoim VIN-em ({n})',
      already_handled: 'Już załatwione dla tego VIN-u',
      in_cart_group: 'Już w Twoim koszyku',
      tag_in_cart: 'W koszyku',
      none_title: 'Nie znaleźliśmy Twojego VIN-u na żadnej z obsługiwanych stron.',
      none_body: 'To dobry wynik. Nie oznacza to, że VIN nie występuje nigdzie w internecie — tylko że żadna ze stron, które obsługujemy, obecnie go nie pokazuje. Jeśli spodziewałeś się czegoś znaleźć, wyślij nam zdjęcie tabliczki VIN, a sprawdzimy ręcznie.',
      selected: 'zaznaczone: {n}',
      selected_none: 'Nic nie zaznaczono',
      add_to_cart: 'Dodaj do koszyka',
      add_n_to_cart: 'Dodaj {n} do koszyka · {price}',
      vehicle_intro: 'To widzi każdy, kto wpisze ten VIN w wyszukiwarce:',
      step_search: 'Przeszukuję Google i publiczne serwisy z ogłoszeniami',
      step_match: 'Porównuję wyniki z serwisami, które obsługujemy',
      step_match_n: 'Dopasowano {n} serwisów, które publikują Twój VIN',
      step_price: 'Sprawdzam ceny i dostępność',
      eta_h: 'Zwykle gotowe w {n} h',
      eta_h_range: 'Zwykle gotowe w {a}–{b} h',
      eta_d: 'Zwykle gotowe w {n} dni',
      eta_d_range: 'Zwykle gotowe w {a}–{b} dni',
      n_sites: 'stron: {n}',
      choose_scope: 'Wybierz zakres usunięcia',
      scope_core: 'Zacznij od najważniejszych',
      scope_recommended: 'Zalecane sprzątanie',
      scope_full: 'Usuń wszystko',
      most_chosen: 'Najczęściej wybierane',
      full_note: 'Obejmuje {n}, które inaczej zostaną w sieci',
      leaves_note: 'Zostawia {n} stron w sieci',
      show_more: 'Pokaż pozostałe {n} · {price}',
      upsell_added: 'Dodane do koszyka — {n} stron, {price}',
      upsell_title: 'Kolejne {n} stron nadal pokazuje ten VIN',
      upsell_body: 'Znalezione pod tym samym VIN-em. Zostaną w sieci, jeśli ich też nie usuniemy.',
      upsell_and_more: 'i jeszcze {n}',
      upsell_cost: 'To dodatkowe {price} — koszyk urośnie do {total}.',
      upsell_add: 'Usuń też pozostałe {n} · {price}',
      upsell_skip: 'Nie, przejdź do koszyka',
      bundle_covers: 'Jedno zamówienie obejmuje {sites}',
      bundle_saving: 'Oszczędzasz {amount}',
      err_not_a_vin: 'Wpisz prawdziwy numer VIN.',
      partial_notice: 'Część wyszukiwarek nie odpowiedziała, więc ta lista może być niepełna.',
      failed_partial: 'Nie udało się dokończyć sprawdzenia, więc na tej liście może brakować stron. Napisz do nas, sprawdzimy ręcznie.',
      photo_n: 'Zdjęcie {n}',
      photo_prev: 'Poprzednie zdjęcie',
      photo_next: 'Następne zdjęcie',
      chip_vin: 'VIN',
      chip_lot: 'Lot',
      chip_auction: 'Aukcja',
      chip_location: 'Lokalizacja',
      chip_sale_date: 'Data sprzedaży',
      chip_sold_for: 'Cena sprzedaży',
      chip_damage: 'Uszkodzenia',
      chip_odometer: 'Przebieg',
      chip_doc: 'Dokument',
      unit_miles: 'mil',
      tag_serp: 'Pierwsza strona Google',
      tag_photos: 'Zdjęcia z aukcji',
      tag_fast: 'Szybkie usunięcie',
      status_removed: 'Usunięte {date}',
      status_verified: 'Usunięte i potwierdzone {date}',
      status_progress: 'W realizacji',
      status_unavailable: 'Zapytaj nas o tę stronę',
      unsupported: 'Twoja przeglądarka jest za stara dla tego narzędzia. Napisz na office@cleanvinusa.com i podaj VIN — sprawdzimy go za Ciebie.'
    },
    es: {
      add_site_open: '¿No está tu sitio? Añádelo',
      add_site_title: 'Añade un sitio de nuestro catálogo',
      add_site_intro: '¿Encontraste tu VIN en un sitio que el análisis no mostró? Elígelo aquí y entra en el mismo pedido.',
      add_site_search: 'Buscar por nombre del sitio',
      add_site_none: 'Nada coincide con ese nombre.',
      add_site_covered: 'Todos los sitios que cubrimos ya están arriba o en tu carrito.',
      add_site_err: 'No pudimos cargar la lista de sitios. Inténtalo de nuevo o envíanos la dirección.',
      add_site_add: 'Añadir {n} · {price}',
      add_site_cancel: 'Cancelar',
      add_site_close: 'Cerrar',
      add_site_soon: 'Consúltanos sobre este sitio',
      add_site_added: 'Se añadieron {n} a tu carrito.',
      auto_cart_adding: 'Añadiendo a tu carrito las {n} páginas donde tu coche sigue apareciendo…',
      auto_cart_none: 'No queda nada — todo lo del correo ya está resuelto.',
      add_site_go: 'Ir al carrito',
      checking: 'Comprobando…',
      err_empty: 'Introduce tu VIN.',
      err_len: 'Un VIN tiene exactamente 17 caracteres. Has introducido {n}.',
      err_ioq: 'Un VIN nunca contiene las letras I, O o Q.',
      err_no_vin_found: 'No hay un VIN de 17 caracteres ahí. Un enlace de lote de Copart o IAAI solo lleva el número de lote — el VIN no está en él. Pega los 17 caracteres, o un enlace de una página donde se vea el VIN.',
      err_rate_limited: 'Has ejecutado esta comprobación varias veces en poco tiempo, así que está pausada un rato. Inténtalo más tarde o escribe a office@cleanvinusa.com con tu VIN y la haremos a mano.',
      err_search_failed: 'Esta vez no pudimos completar la comprobación, así que no podemos decirte si tu VIN está publicado. Inténtalo en un momento o escríbenos y lo revisamos a mano.',
      found_at: 'Encontrado en',
      more_pages: '+{n} más',
      page_removed: 'Esta página ya no existe',
      help_title: '¿No estás seguro o algo no cuadra?',
      err_api: 'No hemos podido completar la comprobación ahora mismo.',
      err_cart: 'No hemos podido añadir los artículos al carrito. Inténtalo de nuevo.',
      retry: 'Reintentar',
      scanning: 'Buscando tu VIN en los resultados de búsqueda…',
      scanning_slow: 'Seguimos buscando — puede tardar hasta un minuto.',
      checked_at: 'Comprobado el {date}, {time}',
      checking_vin: 'VIN comprobado',
      result_title: 'Encontramos tu VIN en {n} sitios',
      result_title_one: 'Encontramos tu VIN en 1 sitio',
      full_cleanup: 'Limpieza completa',
      recommended: 'Primer paso recomendado',
      other_sites: 'Otros sitios donde aparece tu VIN ({n})',
      already_handled: 'Ya gestionado para este VIN',
      in_cart_group: 'Ya en tu carrito',
      tag_in_cart: 'En el carrito',
      none_title: 'No encontramos tu VIN en ninguno de los sitios que cubrimos.',
      none_body: 'Es un buen resultado. No significa que el VIN no exista en ningún sitio de internet, solo que ninguno de los sitios que cubrimos lo muestra ahora. Si esperabas encontrar algo, envíanos una foto de la placa del VIN y lo revisamos a mano.',
      selected: '{n} seleccionados',
      selected_none: 'Nada seleccionado',
      add_to_cart: 'Añadir al carrito',
      add_n_to_cart: 'Añadir {n} al carrito · {price}',
      vehicle_intro: 'Esto es lo que cualquiera puede ver de tu coche buscando este VIN:',
      step_search: 'Buscando en Google y en sitios de anuncios públicos',
      step_match: 'Comparando los resultados con los sitios que cubrimos',
      step_match_n: '{n} sitios encontrados que publican tu VIN',
      step_price: 'Comprobando precios y disponibilidad',
      eta_h: 'Normalmente listo en {n} h',
      eta_h_range: 'Normalmente listo en {a}–{b} h',
      eta_d: 'Normalmente listo en {n} días',
      eta_d_range: 'Normalmente listo en {a}–{b} días',
      n_sites: '{n} sitios',
      choose_scope: 'Elige cuánto quieres eliminar',
      scope_core: 'Empieza por los principales',
      scope_recommended: 'Limpieza recomendada',
      scope_full: 'Eliminar todo',
      most_chosen: 'La opción más elegida',
      full_note: 'Incluye los {n} que si no quedarían publicados',
      leaves_note: 'Deja {n} páginas publicadas',
      show_more: 'Ver los otros {n} · {price}',
      upsell_added: 'Añadido al carrito — {n} páginas, {price}',
      upsell_title: 'Otras {n} páginas siguen mostrando este VIN',
      upsell_body: 'Encontradas con el mismo VIN. Seguirán publicadas si no se eliminan también.',
      upsell_and_more: 'y {n} más',
      upsell_cost: 'Son {price} más, dejando el carrito en {total}.',
      upsell_add: 'Eliminar también las otras {n} · {price}',
      upsell_skip: 'No, gracias, ir al carrito',
      bundle_covers: 'Un solo pedido cubre {sites}',
      bundle_saving: 'Ahorras {amount}',
      err_not_a_vin: 'Introduce un VIN real.',
      partial_notice: 'Algunos buscadores no respondieron, así que esta lista puede estar incompleta.',
      failed_partial: 'No pudimos terminar la comprobación, así que pueden faltar sitios en esta lista. Escríbenos y la revisamos a mano.',
      photo_n: 'Foto {n}',
      photo_prev: 'Foto anterior',
      photo_next: 'Foto siguiente',
      chip_vin: 'VIN',
      chip_lot: 'Lote',
      chip_auction: 'Subasta',
      chip_location: 'Ubicación',
      chip_sale_date: 'Fecha de venta',
      chip_sold_for: 'Vendido por',
      chip_damage: 'Daños',
      chip_odometer: 'Odómetro',
      chip_doc: 'Título',
      unit_miles: 'mi',
      tag_serp: 'Primera página de Google',
      tag_photos: 'Fotos de subasta',
      tag_fast: 'Retirada rápida',
      status_removed: 'Eliminado el {date}',
      status_verified: 'Eliminado y verificado el {date}',
      status_progress: 'En proceso',
      status_unavailable: 'Consúltanos sobre este sitio',
      unsupported: 'Tu navegador es demasiado antiguo para esta herramienta. Escríbenos a office@cleanvinusa.com con tu VIN y haremos la comprobación por ti.'
    },
    ru: {
      add_site_open: 'Вашего сайта нет? Добавьте его',
      add_site_title: 'Добавьте сайт из нашего каталога',
      add_site_intro: 'Нашли свой VIN там, где проверка ничего не показала? Выберите сайт здесь — он попадёт в тот же заказ.',
      add_site_search: 'Поиск по названию сайта',
      add_site_none: 'Ничего не найдено по этому названию.',
      add_site_covered: 'Все сайты, с которыми мы работаем, уже в списке выше или в корзине.',
      add_site_err: 'Не удалось загрузить список сайтов. Попробуйте ещё раз или пришлите нам адрес.',
      add_site_add: 'Добавить {n} · {price}',
      add_site_cancel: 'Отмена',
      add_site_close: 'Закрыть',
      add_site_soon: 'Спросите нас об этом сайте',
      add_site_added: 'Добавлено в корзину: {n}.',
      auto_cart_adding: 'Добавляем в корзину {n} сайтов, где машина всё ещё видна…',
      auto_cart_none: 'Добавлять нечего — всё из того письма уже закрыто.',
      add_site_go: 'Перейти в корзину',
      checking: 'Проверяем…',
      err_empty: 'Введите VIN.',
      err_len: 'VIN состоит ровно из 17 символов. Введено {n}.',
      err_ioq: 'VIN никогда не содержит букв I, O и Q.',
      err_no_vin_found: 'Здесь нет 17-значного VIN. Ссылка на лот Copart или IAAI содержит только номер лота — VIN в ней отсутствует. Вставьте 17 символов или ссылку на страницу, где VIN виден.',
      err_rate_limited: 'Эта проверка запускалась несколько раз за короткое время и временно приостановлена. Попробуйте позже или напишите на office@cleanvinusa.com с номером VIN — проверим вручную.',
      err_search_failed: 'В этот раз проверку завершить не удалось, поэтому мы не можем сказать, есть ли ваш VIN в сети. Попробуйте позже или напишите нам — проверим вручную.',
      found_at: 'Найдено по адресу',
      more_pages: 'и ещё {n}',
      page_removed: 'Этой страницы больше нет',
      help_title: 'Сомневаетесь или что-то выглядит странно?',
      err_api: 'Сейчас не удалось выполнить проверку.',
      err_cart: 'Не удалось добавить в корзину. Попробуйте еще раз.',
      retry: 'Повторить',
      scanning: 'Ищем ваш VIN в результатах поиска…',
      scanning_slow: 'Еще ищем — полная проверка может занять до минуты.',
      checked_at: 'Проверено {date}, {time}',
      checking_vin: 'Проверяем VIN',
      result_title: 'Мы нашли ваш VIN на {n} сайтах',
      result_title_one: 'Мы нашли ваш VIN на 1 сайте',
      full_cleanup: 'Полная очистка',
      recommended: 'Рекомендуемый первый шаг',
      other_sites: 'Другие сайты с вашим VIN ({n})',
      already_handled: 'Уже сделано для этого VIN',
      in_cart_group: 'Уже в вашей корзине',
      tag_in_cart: 'В корзине',
      none_title: 'Мы не нашли ваш VIN ни на одном из сайтов, с которыми работаем.',
      none_body: 'Это хороший результат. Это не значит, что VIN отсутствует везде в интернете — только что ни один из сайтов, которые мы охватываем, сейчас его не показывает. Если вы ожидали что-то найти, пришлите фото таблички VIN, и мы проверим вручную.',
      selected: 'выбрано: {n}',
      selected_none: 'Ничего не выбрано',
      add_to_cart: 'В корзину',
      add_n_to_cart: 'В корзину: {n} · {price}',
      vehicle_intro: 'Вот что видит любой, кто ищет этот VIN:',
      step_search: 'Ищем в Google и на публичных сайтах объявлений',
      step_match: 'Сверяем найденное с сайтами, которые мы обслуживаем',
      step_match_n: 'Найдено сайтов с вашим VIN: {n}',
      step_price: 'Проверяем цены и наличие',
      eta_h: 'Обычно готово за {n} ч',
      eta_h_range: 'Обычно готово за {a}–{b} ч',
      eta_d: 'Обычно готово за {n} дн.',
      eta_d_range: 'Обычно готово за {a}–{b} дн.',
      n_sites: 'сайтов: {n}',
      choose_scope: 'Выберите объём удаления',
      scope_core: 'Начните с главных',
      scope_recommended: 'Рекомендуемая очистка',
      scope_full: 'Удалить всё',
      most_chosen: 'Выбирают чаще всего',
      full_note: 'Включает ещё {n}, которые иначе останутся в сети',
      leaves_note: 'Оставляет {n} страниц в сети',
      show_more: 'Показать остальные {n} · {price}',
      upsell_added: 'Добавлено в корзину — {n} страниц, {price}',
      upsell_title: 'Ещё {n} страниц показывают этот VIN',
      upsell_body: 'Найдены по тому же VIN. Останутся в сети, если их тоже не удалить.',
      upsell_and_more: 'и ещё {n}',
      upsell_cost: 'Это ещё {price} — корзина станет {total}.',
      upsell_add: 'Удалить также остальные {n} · {price}',
      upsell_skip: 'Нет, перейти в корзину',
      bundle_covers: 'Один заказ покрывает {sites}',
      bundle_saving: 'Экономия {amount}',
      err_not_a_vin: 'Введите настоящий VIN.',
      partial_notice: 'Часть поисковых систем не ответила, поэтому список может быть неполным.',
      failed_partial: 'Мы не смогли завершить проверку, поэтому в списке могут отсутствовать сайты. Напишите нам — проверим вручную.',
      photo_n: 'Фото {n}',
      photo_prev: 'Предыдущее фото',
      photo_next: 'Следующее фото',
      chip_vin: 'VIN',
      chip_lot: 'Лот',
      chip_auction: 'Аукцион',
      chip_location: 'Локация',
      chip_sale_date: 'Дата продажи',
      chip_sold_for: 'Цена продажи',
      chip_damage: 'Повреждения',
      chip_odometer: 'Пробег',
      chip_doc: 'Документ',
      unit_miles: 'миль',
      tag_serp: 'Первая страница Google',
      tag_photos: 'Фото с аукциона',
      tag_fast: 'Быстрое удаление',
      status_removed: 'Удалено {date}',
      status_verified: 'Удалено и проверено {date}',
      status_progress: 'В работе',
      status_unavailable: 'Спросите нас об этом сайте',
      unsupported: 'Ваш браузер слишком старый для этого инструмента. Напишите на office@cleanvinusa.com и укажите VIN — мы проверим его за вас.'
    },
    de: {
      add_site_open: 'Ihre Seite fehlt? Hinzufügen',
      add_site_title: 'Seite aus unserem Katalog hinzufügen',
      add_site_intro: 'Ihre FIN auf einer Seite gefunden, die der Scan nicht zeigt? Wählen Sie sie hier — sie kommt in dieselbe Bestellung.',
      add_site_search: 'Nach Seitenname suchen',
      add_site_none: 'Kein Treffer für diesen Namen.',
      add_site_covered: 'Alle Seiten, die wir abdecken, stehen bereits oben oder liegen im Warenkorb.',
      add_site_err: 'Die Seitenliste konnte nicht geladen werden. Bitte erneut versuchen oder senden Sie uns die Adresse.',
      add_site_add: '{n} hinzufügen · {price}',
      add_site_cancel: 'Abbrechen',
      add_site_close: 'Schließen',
      add_site_soon: 'Fragen Sie uns zu dieser Seite',
      add_site_added: '{n} zum Warenkorb hinzugefügt.',
      auto_cart_adding: '{n} Seiten, auf denen Ihr Auto noch zu sehen ist, kommen in den Warenkorb…',
      auto_cart_none: 'Nichts mehr zu tun — alles aus dieser E-Mail ist bereits erledigt.',
      add_site_go: 'Zum Warenkorb',
      checking: 'Wird geprüft…',
      err_empty: 'Bitte geben Sie Ihre VIN ein.',
      err_len: 'Eine VIN hat genau 17 Zeichen. Sie haben {n} eingegeben.',
      err_ioq: 'Eine VIN enthält niemals die Buchstaben I, O oder Q.',
      err_no_vin_found: 'Darin ist keine 17-stellige VIN enthalten. Ein Lot-Link von Copart oder IAAI enthält nur eine Lot-Nummer — die VIN steht nicht darin. Fügen Sie die 17 Zeichen ein oder einen Link von einer Seite, auf der die VIN zu sehen ist.',
      err_rate_limited: 'Sie haben diese Prüfung in kurzer Zeit mehrfach ausgeführt, deshalb ist sie für eine Weile pausiert. Versuchen Sie es später erneut oder schreiben Sie an office@cleanvinusa.com mit Ihrer VIN, dann prüfen wir sie von Hand.',
      err_search_failed: 'Wir konnten die Prüfung diesmal nicht abschließen — daher können wir Ihnen nicht sagen, ob Ihre VIN im Netz zu finden ist. Bitte versuchen Sie es gleich noch einmal oder kontaktieren Sie uns, dann prüfen wir es von Hand.',
      found_at: 'Gefunden auf',
      more_pages: '+{n} weitere',
      page_removed: 'Diese Seite ist nicht mehr online',
      help_title: 'Unsicher, oder stimmt etwas nicht?',
      err_api: 'Wir konnten die Prüfung gerade nicht abschließen.',
      err_cart: 'Wir konnten die Positionen nicht in Ihren Warenkorb legen. Bitte versuchen Sie es erneut.',
      retry: 'Erneut versuchen',
      scanning: 'Suchergebnisse werden nach Ihrer VIN durchsucht…',
      scanning_slow: 'Die Suche läuft noch — eine vollständige Prüfung kann bis zu einer Minute dauern.',
      checked_at: 'Geprüft am {date}, {time}',
      checking_vin: 'VIN wird geprüft',
      result_title: 'Wir haben Ihre VIN auf {n} Seiten gefunden',
      result_title_one: 'Wir haben Ihre VIN auf 1 Seite gefunden',
      full_cleanup: 'Vollständige Bereinigung',
      recommended: 'Empfohlener erster Schritt',
      other_sites: 'Weitere Seiten, auf denen Ihre VIN erscheint ({n})',
      already_handled: 'Für diese VIN bereits erledigt',
      in_cart_group: 'Bereits in Ihrem Warenkorb',
      tag_in_cart: 'Im Warenkorb',
      none_title: 'Wir haben Ihre VIN auf keiner der von uns abgedeckten Seiten gefunden.',
      none_body: 'Das ist ein gutes Ergebnis. Es bedeutet nicht, dass die VIN auf keiner Seite im Internet vorkommt — nur, dass keine der von uns abgedeckten Seiten sie derzeit anzeigt. Wenn Sie einen Treffer erwartet hätten, senden Sie uns ein Foto des VIN-Schilds, dann prüfen wir es von Hand.',
      selected: '{n} ausgewählt',
      selected_none: 'Nichts ausgewählt',
      add_to_cart: 'In den Warenkorb',
      add_n_to_cart: '{n} in den Warenkorb · {price}',
      vehicle_intro: 'Das sieht jeder über Ihr Auto, der nach dieser VIN sucht:',
      step_search: 'Google und öffentliche Inseratsseiten werden durchsucht',
      step_match: 'Abgleich der Funde mit den von uns abgedeckten Seiten',
      step_match_n: '{n} Seiten gefunden, die Ihre VIN veröffentlichen',
      step_price: 'Preise und Verfügbarkeit werden geprüft',
      eta_h: 'Meist innerhalb von {n} Std. erledigt',
      eta_h_range: 'Meist in {a}–{b} Std. erledigt',
      eta_d: 'Meist innerhalb von {n} Tagen erledigt',
      eta_d_range: 'Meist in {a}–{b} Tagen erledigt',
      n_sites: '{n} Seiten',
      choose_scope: 'Wählen Sie den Umfang der Entfernung',
      scope_core: 'Mit den wichtigsten beginnen',
      scope_recommended: 'Empfohlene Bereinigung',
      scope_full: 'Alles entfernen',
      most_chosen: 'Am häufigsten gewählt',
      full_note: 'Deckt die {n} ab, die sonst online blieben',
      leaves_note: 'Lässt {n} Seiten online',
      show_more: 'Die anderen {n} anzeigen · {price}',
      upsell_added: 'In den Warenkorb gelegt — {n} Seiten, {price}',
      upsell_title: '{n} weitere Seiten zeigen diese VIN noch',
      upsell_body: 'Zur selben VIN gefunden. Sie bleiben online, wenn sie nicht ebenfalls entfernt werden.',
      upsell_and_more: 'und {n} weitere',
      upsell_cost: 'Das sind {price} mehr, damit steigt Ihr Warenkorb auf {total}.',
      upsell_add: 'Die anderen {n} ebenfalls entfernen · {price}',
      upsell_skip: 'Nein danke, zum Warenkorb',
      bundle_covers: 'Eine Bestellung deckt {sites} ab',
      bundle_saving: 'Spart {amount}',
      err_not_a_vin: 'Bitte geben Sie eine echte VIN ein.',
      partial_notice: 'Einige Suchmaschinen haben nicht geantwortet, daher kann diese Liste unvollständig sein.',
      failed_partial: 'Wir konnten die Prüfung nicht abschließen, daher fehlen in dieser Liste möglicherweise Seiten. Kontaktieren Sie uns, dann prüfen wir es von Hand.',
      photo_n: 'Foto {n}',
      photo_prev: 'Vorheriges Foto',
      photo_next: 'Nächstes Foto',
      chip_vin: 'VIN',
      chip_lot: 'Lot',
      chip_auction: 'Auktion',
      chip_location: 'Standort',
      chip_sale_date: 'Verkaufsdatum',
      chip_sold_for: 'Verkauft für',
      chip_damage: 'Schaden',
      chip_odometer: 'Tachostand',
      chip_doc: 'Fahrzeugtitel',
      unit_miles: 'mi',
      tag_serp: 'Erste Google-Seite',
      tag_photos: 'Auktionsfotos',
      tag_fast: 'Schnelle Entfernung',
      status_removed: 'Entfernt am {date}',
      status_verified: 'Entfernt und geprüft am {date}',
      status_progress: 'In Bearbeitung',
      status_unavailable: 'Fragen Sie uns zu dieser Seite',
      unsupported: 'Ihr Browser ist zu alt für dieses Tool. Bitte schreiben Sie an office@cleanvinusa.com mit Ihrer VIN, dann führen wir die Prüfung für Sie durch.'
    },
    ar: {
      add_site_open: 'موقعك غير مدرج؟ أضِفه',
      add_site_title: 'أضِف موقعًا من قائمتنا',
      add_site_intro: 'وجدت رقم هيكل سيارتك على موقع لم يظهر في الفحص؟ اخترْه هنا ليُضاف إلى الطلب نفسه.',
      add_site_search: 'ابحث باسم الموقع',
      add_site_none: 'لا نتائج مطابقة لهذا الاسم.',
      add_site_covered: 'كل المواقع التي نغطيها موجودة بالفعل أعلاه أو في سلتك.',
      add_site_err: 'تعذّر تحميل قائمة المواقع. حاول مرة أخرى أو أرسل لنا العنوان.',
      add_site_add: 'أضِف {n} · {price}',
      add_site_cancel: 'إلغاء',
      add_site_close: 'إغلاق',
      add_site_soon: 'اسألنا عن هذا الموقع',
      add_site_added: 'تمت إضافة {n} إلى سلتك.',
      auto_cart_adding: 'نضيف إلى سلتك {n} صفحات ما زالت تعرض سيارتك…',
      auto_cart_none: 'لا شيء متبقٍ — كل ما في تلك الرسالة تمت معالجته.',
      add_site_go: 'الذهاب إلى السلة',
      checking: 'جارٍ الفحص…',
      err_empty: 'يرجى إدخال رقم VIN الخاص بك.',
      err_len: 'رقم VIN يتكوّن من 17 خانة بالضبط. لقد أدخلت {n}.',
      err_ioq: 'رقم VIN لا يحتوي أبداً على الحروف I أو O أو Q.',
      err_no_vin_found: 'لا يوجد رقم VIN من 17 خانة في ما أدخلته. رابط القطعة في Copart أو IAAI يحمل رقم القطعة فقط — رقم VIN ليس موجوداً فيه. الصق الخانات الـ 17، أو رابط صفحة تعرض رقم VIN.',
      err_rate_limited: 'لقد أجريت هذا الفحص عدة مرات خلال فترة قصيرة، لذلك تم إيقافه مؤقتاً لبعض الوقت. حاول مرة أخرى لاحقاً، أو راسلنا على office@cleanvinusa.com مع رقم VIN وسنجري الفحص يدوياً.',
      err_search_failed: 'لم نتمكن من إتمام الفحص هذه المرة — لذلك لا يمكننا أن نخبرك ما إذا كان رقم VIN الخاص بك موجوداً على الإنترنت. يرجى المحاولة بعد قليل أو التواصل معنا وسنجري الفحص يدوياً.',
      found_at: 'وُجد في',
      more_pages: '+{n} أخرى',
      page_removed: 'هذه الصفحة لم تعد موجودة',
      help_title: 'غير متأكد، أو هناك شيء يبدو غير صحيح؟',
      err_api: 'لم نتمكن من إتمام الفحص الآن.',
      err_cart: 'لم نتمكن من إضافة العناصر إلى سلتك. يرجى المحاولة مرة أخرى.',
      retry: 'حاول مرة أخرى',
      scanning: 'نبحث في نتائج البحث عن رقم VIN الخاص بك…',
      scanning_slow: 'ما زال البحث جارياً — قد يستغرق الفحص الكامل حتى دقيقة.',
      checked_at: 'تم الفحص في {date}، {time}',
      checking_vin: 'فحص رقم VIN',
      result_title: 'وجدنا رقم VIN الخاص بك على {n} من المواقع',
      result_title_one: 'وجدنا رقم VIN الخاص بك على موقع واحد',
      full_cleanup: 'تنظيف كامل',
      recommended: 'الخطوة الأولى الموصى بها',
      other_sites: 'مواقع أخرى يظهر فيها رقم VIN الخاص بك ({n})',
      already_handled: 'تمت معالجته لهذا الـ VIN',
      in_cart_group: 'موجود في سلتك',
      tag_in_cart: 'في سلتك',
      none_title: 'لم نجد رقم VIN الخاص بك على أي من المواقع التي نغطيها.',
      none_body: 'هذه نتيجة جيدة. لا تعني أن رقم VIN غير موجود على كل موقع في الإنترنت — بل فقط أن أياً من المواقع التي نغطيها لا يعرضه في الوقت الحالي. إذا كنت تتوقع العثور على شيء، أرسل لنا صورة للوحة رقم VIN وسنتحقق منها يدوياً.',
      selected: 'تم اختيار {n}',
      selected_none: 'لم يتم اختيار شيء',
      add_to_cart: 'أضف إلى السلة',
      add_n_to_cart: 'أضف {n} إلى السلة · {price}',
      vehicle_intro: 'هذا ما يستطيع أي شخص رؤيته عن سيارتك عند البحث عن رقم VIN هذا:',
      step_search: 'البحث في Google ومواقع الإعلانات العامة',
      step_match: 'مطابقة ما نجده مع المواقع التي نغطيها',
      step_match_n: 'طابقنا {n} من المواقع التي تنشر رقم VIN الخاص بك',
      step_price: 'التحقق من الأسعار والتوفّر',
      eta_h: 'عادةً يتم خلال {n} ساعة',
      eta_h_range: 'عادةً يتم خلال {a}–{b} ساعة',
      eta_d: 'عادةً يتم خلال {n} يوم',
      eta_d_range: 'عادةً يتم خلال {a}–{b} يوم',
      n_sites: '{n} موقع',
      choose_scope: 'اختر حجم الإزالة',
      scope_core: 'ابدأ بالمواقع الرئيسية',
      scope_recommended: 'التنظيف الموصى به',
      scope_full: 'إزالة كل شيء',
      most_chosen: 'الأكثر اختيارًا',
      full_note: 'يشمل الـ {n} التي كانت ستبقى على الإنترنت',
      leaves_note: 'يترك {n} من الصفحات على الإنترنت',
      show_more: 'اعرض الـ {n} الأخرى · {price}',
      upsell_added: 'تمت الإضافة إلى السلة — {n} من الصفحات، {price}',
      upsell_title: 'ما زالت {n} من الصفحات الأخرى تعرض هذا الـ VIN',
      upsell_body: 'وُجدت بنفس رقم VIN. ستبقى على الإنترنت ما لم تتم إزالتها أيضاً.',
      upsell_and_more: 'و{n} أخرى',
      upsell_cost: 'هذا يعني {price} إضافية، ليصبح إجمالي سلتك {total}.',
      upsell_add: 'أزل أيضاً الـ {n} الأخرى · {price}',
      upsell_skip: 'لا شكراً، انتقل إلى السلة',
      bundle_covers: 'طلب واحد يغطي {sites}',
      bundle_saving: 'يوفّر {amount}',
      err_not_a_vin: 'يرجى إدخال رقم VIN حقيقي.',
      partial_notice: 'بعض محركات البحث لم تستجب، لذلك قد تكون هذه القائمة غير مكتملة.',
      failed_partial: 'لم نتمكن من إنهاء الفحص، لذلك قد تنقص هذه القائمة بعض المواقع. تواصل معنا وسنجري الفحص يدوياً.',
      photo_n: 'صورة {n}',
      photo_prev: 'الصورة السابقة',
      photo_next: 'الصورة التالية',
      chip_vin: 'VIN',
      chip_lot: 'رقم القطعة',
      chip_auction: 'المزاد',
      chip_location: 'الموقع',
      chip_sale_date: 'تاريخ البيع',
      chip_sold_for: 'سعر البيع',
      chip_damage: 'الضرر',
      chip_odometer: 'عداد المسافة',
      chip_doc: 'سند الملكية',
      unit_miles: 'ميل',
      tag_serp: 'الصفحة الأولى في Google',
      tag_photos: 'صور المزاد',
      tag_fast: 'إزالة سريعة',
      status_removed: 'تمت الإزالة {date}',
      status_verified: 'تمت الإزالة والتحقق منها {date}',
      status_progress: 'قيد التنفيذ',
      status_unavailable: 'اسألنا عن هذا الموقع',
      unsupported: 'متصفحك قديم جداً لاستخدام هذه الأداة. يرجى مراسلتنا على office@cleanvinusa.com مع رقم VIN وسنجري الفحص نيابةً عنك.'
    },
    ka: {
      add_site_open: 'თქვენი საიტი არ არის? დაამატეთ',
      add_site_title: 'დაამატეთ საიტი ჩვენი კატალოგიდან',
      add_site_intro: 'იპოვეთ თქვენი VIN საიტზე, რომელიც შემოწმებამ არ აჩვენა? აირჩიეთ აქ — იგივე შეკვეთაში მოხვდება.',
      add_site_search: 'ძებნა საიტის სახელით',
      add_site_none: 'ამ სახელით არაფერი მოიძებნა.',
      add_site_covered: 'ყველა საიტი, რომელსაც ვფარავთ, უკვე ზემოთაა ან თქვენს კალათაშია.',
      add_site_err: 'საიტების სია ვერ ჩაიტვირთა. სცადეთ ხელახლა ან გამოგვიგზავნეთ მისამართი.',
      add_site_add: 'დამატება {n} · {price}',
      add_site_cancel: 'გაუქმება',
      add_site_close: 'დახურვა',
      add_site_soon: 'გვკითხეთ ამ საიტის შესახებ',
      add_site_added: 'კალათაში დაემატა: {n}.',
      auto_cart_adding: 'კალათაში ემატება {n} საიტი, სადაც თქვენი მანქანა ჯერ კიდევ ჩანს…',
      auto_cart_none: 'დასამატებელი აღარაფერია — ყველაფერი იმ წერილიდან უკვე მოგვარებულია.',
      add_site_go: 'კალათაში გადასვლა',
      checking: 'მიმდინარეობს შემოწმება…',
      err_empty: 'გთხოვთ, შეიყვანოთ თქვენი VIN.',
      err_len: 'VIN ზუსტად 17 სიმბოლოსგან შედგება. თქვენ შეიყვანეთ {n}.',
      err_ioq: 'VIN არასოდეს შეიცავს ასოებს I, O და Q.',
      err_no_vin_found: 'აქ 17-სიმბოლოიანი VIN არ არის. Copart-ის ან IAAI-ის ლოტის ბმული მხოლოდ ლოტის ნომერს შეიცავს — VIN მასში არ წერია. ჩასვით 17 სიმბოლო ან ბმული იმ გვერდიდან, სადაც VIN ჩანს.',
      err_rate_limited: 'მოკლე დროში რამდენჯერმე გაუშვით შემოწმება, ამიტომ ის ცოტა ხნით შეჩერებულია. სცადეთ მოგვიანებით ან მოგვწერეთ office@cleanvinusa.com თქვენი VIN-ით და ხელით შევამოწმებთ.',
      err_search_failed: 'ამჯერად შემოწმება ვერ დავასრულეთ — ამიტომ ვერ გეტყვით, ჩანს თუ არა თქვენი VIN ინტერნეტში. სცადეთ ცოტა ხანში ხელახლა ან დაგვიკავშირდით და ხელით შევამოწმებთ.',
      found_at: 'ნაპოვნია აქ',
      more_pages: 'კიდევ {n}',
      page_removed: 'ეს გვერდი წაშლილია',
      help_title: 'გაქვთ ეჭვი ან რაღაც არასწორად გამოიყურება?',
      err_api: 'ამჟამად შემოწმება ვერ დავასრულეთ.',
      err_cart: 'პოზიციები კალათაში ვერ დავამატეთ. გთხოვთ, სცადოთ ხელახლა.',
      retry: 'სცადეთ ხელახლა',
      scanning: 'ვამოწმებთ ძიების შედეგებს თქვენს VIN-ზე…',
      scanning_slow: 'შემოწმება ჯერ მიმდინარეობს — სრულ შემოწმებას შეიძლება ერთ წუთამდე დასჭირდეს.',
      checked_at: 'შემოწმებულია {date}, {time}',
      checking_vin: 'მოწმდება VIN',
      result_title: 'თქვენი VIN ვიპოვეთ {n} საიტზე',
      result_title_one: 'თქვენი VIN ვიპოვეთ 1 საიტზე',
      full_cleanup: 'სრული გასუფთავება',
      recommended: 'რეკომენდებული პირველი ნაბიჯი',
      other_sites: 'სხვა საიტები, სადაც თქვენი VIN ჩანს ({n})',
      already_handled: 'ამ VIN-ისთვის უკვე დამუშავებულია',
      in_cart_group: 'უკვე კალათაშია',
      tag_in_cart: 'კალათაშია',
      none_title: 'თქვენი VIN ჩვენ მიერ დაფარულ არცერთ საიტზე არ მოიძებნა.',
      none_body: 'ეს კარგი შედეგია. ეს არ ნიშნავს, რომ VIN ინტერნეტის ყველა საიტიდან გამქრალია — მხოლოდ იმას, რომ ჩვენ მიერ დაფარული საიტებიდან ამჟამად არცერთი არ აჩვენებს მას. თუ რაიმეს პოვნას ელოდით, გამოგვიგზავნეთ VIN-ის ფირფიტის ფოტო და ხელით შევამოწმებთ.',
      selected: 'არჩეულია {n}',
      selected_none: 'არაფერია არჩეული',
      add_to_cart: 'კალათაში დამატება',
      add_n_to_cart: 'დაამატეთ {n} კალათაში · {price}',
      vehicle_intro: 'ეს არის ის, რასაც ნებისმიერი ხედავს თქვენს მანქანაზე ამ VIN-ის მოძიებისას:',
      step_search: 'ვეძებთ Google-სა და საჯარო განცხადებების საიტებზე',
      step_match: 'ნაპოვნს ვადარებთ ჩვენ მიერ დაფარულ საიტებს',
      step_match_n: 'დადგინდა {n} საიტი, რომელიც აქვეყნებს თქვენს VIN-ს',
      step_price: 'ვამოწმებთ ფასებსა და ხელმისაწვდომობას',
      eta_h: 'ჩვეულებრივ სრულდება {n} სთ-ში',
      eta_h_range: 'ჩვეულებრივ სრულდება {a}–{b} სთ-ში',
      eta_d: 'ჩვეულებრივ სრულდება {n} დღეში',
      eta_d_range: 'ჩვეულებრივ სრულდება {a}–{b} დღეში',
      n_sites: '{n} საიტი',
      choose_scope: 'აირჩიეთ, რამდენი წაიშალოს',
      scope_core: 'დაიწყეთ ძირითადებით',
      scope_recommended: 'რეკომენდებული გასუფთავება',
      scope_full: 'ყველაფრის წაშლა',
      most_chosen: 'ყველაზე ხშირად ირჩევენ',
      full_note: 'მოიცავს იმ {n}-საც, რომელიც სხვა შემთხვევაში ონლაინ დარჩებოდა',
      leaves_note: 'ონლაინ დარჩება {n} გვერდი',
      show_more: 'აჩვენეთ დანარჩენი {n} · {price}',
      upsell_added: 'დაემატა კალათაში — {n} გვერდი, {price}',
      upsell_title: 'კიდევ {n} გვერდი აჩვენებს ამ VIN-ს',
      upsell_body: 'ნაპოვნია იმავე VIN-ით. ისინი ონლაინ დარჩება, თუ არ წაიშლება.',
      upsell_and_more: 'და კიდევ {n}',
      upsell_cost: 'ეს არის დამატებით {price}, კალათა გახდება {total}.',
      upsell_add: 'დანარჩენი {n}-იც წაშალეთ · {price}',
      upsell_skip: 'არა, გადავიდეთ კალათაში',
      bundle_covers: 'ერთი შეკვეთა მოიცავს: {sites}',
      bundle_saving: 'დაზოგავთ {amount}',
      err_not_a_vin: 'გთხოვთ, შეიყვანოთ ნამდვილი VIN.',
      partial_notice: 'ზოგიერთმა საძიებო სისტემამ პასუხი არ დააბრუნა, ამიტომ ეს სია შეიძლება არასრული იყოს.',
      failed_partial: 'შემოწმება ვერ დავასრულეთ, ამიტომ სიაში შეიძლება საიტები აკლდეს. დაგვიკავშირდით და ხელით შევამოწმებთ.',
      photo_n: 'ფოტო {n}',
      photo_prev: 'წინა ფოტო',
      photo_next: 'შემდეგი ფოტო',
      chip_vin: 'VIN',
      chip_lot: 'ლოტი',
      chip_auction: 'აუქციონი',
      chip_location: 'ლოკაცია',
      chip_sale_date: 'გაყიდვის თარიღი',
      chip_sold_for: 'გაყიდვის ფასი',
      chip_damage: 'დაზიანება',
      chip_odometer: 'გარბენი',
      chip_doc: 'ტაიტლი',
      unit_miles: 'მილი',
      tag_serp: 'Google-ის პირველი გვერდი',
      tag_photos: 'აუქციონის ფოტოები',
      tag_fast: 'სწრაფი წაშლა',
      status_removed: 'წაშლილია {date}',
      status_verified: 'წაშლილია და გადამოწმებულია {date}',
      status_progress: 'მიმდინარეობს',
      status_unavailable: 'ამ საიტის შესახებ გვკითხეთ',
      unsupported: 'თქვენი ბრაუზერი ამ ხელსაწყოსთვის ძალიან ძველია. გთხოვთ, მოგვწეროთ office@cleanvinusa.com თქვენი VIN-ით და შემოწმებას ჩვენ ჩავატარებთ.'
    }
  };

  var LANG = pickLang();

  function pickLang() {
    var raw = (document.documentElement.getAttribute('lang') || CFG.lang || 'en');
    var code = String(raw).toLowerCase().slice(0, 2);
    return STRINGS[code] ? code : 'en';
  }

  function t(key, vars) {
    var pack = STRINGS[LANG] || {};
    var s = pack[key];
    if (s === undefined) s = STRINGS.en[key];
    if (s === undefined) s = key;
    if (vars) {
      var k;
      for (k in vars) {
        if (Object.prototype.hasOwnProperty.call(vars, k)) {
          s = s.split('{' + k + '}').join(String(vars[k]));
        }
      }
    }
    return s;
  }

  /* ---------------------------------------------------------------
   * Small helpers
   * ------------------------------------------------------------- */

  function $(id) { return document.getElementById(id); }
  function isArray(v) { return Object.prototype.toString.call(v) === '[object Array]'; }
  function log() { if (CFG.debug && window.console && console.log) console.log.apply(console, arguments); }
  function warn(a, b) { if (window.console && console.error) console.error('[vin-check]', a, b || ''); }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined && text !== null && text !== '') {
      n.appendChild(document.createTextNode(String(text)));
    }
    return n;
  }

  function clear(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  function routesRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return CFG.routesRoot || '/';
  }

  function activeCurrency() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return CFG.currency || 'USD';
  }

  function money(cents) {
    var amount = (Number(cents) || 0) / 100;
    try {
      if (window.Intl && Intl.NumberFormat) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: activeCurrency(),
          minimumFractionDigits: 2
        }).format(amount);
      }
    } catch (e) { /* fall through */ }
    return '$' + amount.toFixed(2);
  }

  function safeUrl(u) {
    var s = String(u || '');
    return /^https?:\/\//i.test(s) || s.indexOf('//') === 0 ? s : '';
  }

  function track(name, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: name };
      var k;
      if (params) {
        for (k in params) {
          if (Object.prototype.hasOwnProperty.call(params, k)) payload[k] = params[k];
        }
      }
      window.dataLayer.push(payload);
      if (typeof window.gtag === 'function') window.gtag('event', name, params || {});
    } catch (e) { /* analytics must never break the widget */ }
  }

  function fetchWithTimeout(url, options, ms) {
    var opts = options || {};
    var controller = null;
    if (window.AbortController) {
      controller = new AbortController();
      opts.signal = controller.signal;
    }
    return new Promise(function (resolve, reject) {
      var done = false;
      var timer = window.setTimeout(function () {
        if (done) return;
        done = true;
        if (controller) { try { controller.abort(); } catch (e) {} }
        reject(new Error('timeout'));
      }, ms || REQUEST_TIMEOUT_MS);

      window.fetch(url, opts).then(function (r) {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        resolve(r);
      })['catch'](function (err) {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        reject(err);
      });
    });
  }

  /* ---------------------------------------------------------------
   * API response normalisation (kept from the live widget)
   * ------------------------------------------------------------- */

  function getApiData(json, vin) {
    if (!json || typeof json !== 'object') return {};
    if (json.data) return json.data;
    if (vin && json[vin] && json[vin].data) return json[vin].data;
    var keys = Object.keys(json);
    if (keys.length && json[keys[0]] && json[keys[0]].data) return json[keys[0]].data;
    return json;
  }

  /* The prices on this page come from Shopify, never from the checker's own
   * table, because Shopify is what the customer is actually charged at
   * checkout. That table drifted below Shopify on 40 of 51 rows once already
   * and the page quoted totals nobody was ever billed.
   *
   * Reading it anyway, purely to compare: if the two disagree again we want to
   * find out from a counter, not from a customer. `product_info[1]` is costs
   * in dollars with the grand total as the last element; ours is in cents.
   * Nothing here changes what is displayed. */
  function checkPriceDrift(apiData) {
    var info = apiData.product_info;
    if (!isArray(info) || !isArray(info[0]) || !isArray(info[1])) return;
    var costs = info[1];
    if (costs.length !== info[0].length + 1) return;   // shape we do not know
    var apiTotalCents = Math.round(Number(costs[costs.length - 1]) * 100);
    if (!apiTotalCents || isNaN(apiTotalCents)) return;
    state.apiTotalCents = apiTotalCents;
  }

  function reportPriceDrift(items) {
    if (!state.apiTotalCents) return;
    var ours = 0;
    for (var i = 0; i < items.length; i++) {
      if (!items[i].missing && items[i].available && !items[i].isBundle) ours += items[i].price;
    }
    // Bundles legitimately lower our total, and so do already-removed sites;
    // only compare when neither is in play.
    var hasBundle = false, hasHandled = false;
    for (var j = 0; j < items.length; j++) {
      if (items[j].isBundle) hasBundle = true;
      if (items[j].done || items[j].pending || items[j].missing) hasHandled = true;
    }
    if (hasBundle || hasHandled) return;
    if (Math.abs(ours - state.apiTotalCents) <= 1) return;

    warn('price drift: checker says ' + (state.apiTotalCents / 100) +
         ', Shopify says ' + (ours / 100) + ' — showing Shopify, it is what gets charged');
    track('price_drift', {
      checker_total: state.apiTotalCents / 100,
      shopify_total: ours / 100,
      currency: activeCurrency()
    });
  }

  // handle -> the checker's own site_id, filled while parsing the check result.
  // Kept so the ORDER carries site identity itself, instead of the panel having
  // to infer it from a product handle that marketing is free to rename.
  var siteIdByHandle = {};

  function extractHandles(apiData) {
    var info = apiData.product_info || apiData.product_handles || apiData.products || [];
    var raw = [];
    if (info.length && isArray(info[0])) raw = info[0];
    else if (isArray(info)) raw = info;

    var out = [], ids = {};
    for (var i = 0; i < raw.length; i++) {
      var item = raw[i];
      var handle = '';
      if (typeof item === 'string') handle = item;
      else if (item && typeof item === 'object') handle = item.handle || item.product_handle || item.slug || '';
      handle = String(handle || '').replace(/^\s+|\s+$/g, '');
      // Opportunistic: only used when the checker actually sends an id. When it
      // does not, cartPayload falls back to the handle and nothing breaks.
      if (handle && item && typeof item === 'object') {
        var sid = item.site_id || item.siteId || '';
        if (sid) ids[handle] = String(sid);
      }
      if (handle && out.indexOf(handle) === -1) out.push(handle);
    }
    siteIdByHandle = ids;
    return out;
  }

  function hasValidVehicleData(vd) {
    if (!vd) return false;
    var msg = null;
    if (typeof vd === 'string') msg = vd.toLowerCase();
    else if (typeof vd.message === 'string') msg = vd.message.toLowerCase();
    if (msg !== null) {
      if (msg.indexOf('no vehicle data found') !== -1) return false;
      if (msg.indexOf('vehicle not found') !== -1) return false;
      if (msg.indexOf('no records') !== -1) return false;
      if (typeof vd === 'string') return true;
    }
    if (isArray(vd.records) && vd.records.length) return isRenderableRecord(vd.records[0]);
    if (typeof vd.totalRecords === 'number') return vd.totalRecords > 0;
    if (typeof vd.record_count === 'number') return vd.record_count > 0;
    if (vd.primary_record) return true;
    if (isArray(vd.imageUrls) && vd.imageUrls.length) return true;
    if (isArray(vd.photos) && vd.photos.length) return true;
    if (isArray(vd.images) && vd.images.length) return true;
    return false;
  }

  // The one mistake this widget can never make: showing someone else's car
  // under the customer's VIN. The cleaned.vin contract says the API must 404
  // on a mismatch rather than return the nearest record — but "must" is a
  // promise about someone else's code, and this is the screen the customer
  // actually looks at. A record with no VIN at all is fine (the API answered
  // the VIN we asked about); a record with a DIFFERENT VIN is not.
  function vinsAgree(candidate, vin) {
    var got = normalizeVin(candidate || '');
    if (!got) return true;                       // nothing claimed, nothing to contradict
    return got === normalizeVin(vin || '');
  }

  // Returns the vehicle payload only when it is safe to render, otherwise null.
  // Single source of truth: the card, the partner link and the analytics event
  // must never disagree about whether we have usable vehicle data.
  function usableVehicle(apiData, vin) {
    var vd = apiData.vehicle_details || apiData.vehicle || apiData.vehicle_data || null;
    if (!hasValidVehicleData(vd)) return null;
    if (vd && typeof vd === 'object') {
      var record = vd.primary_record ||
                   (isArray(vd.records) && vd.records.length ? vd.records[0] : null) ||
                   vd.vehicle || vd;
      var claimed = (record && typeof record === 'object' && record.vin) || vd.vin || '';
      if (!vinsAgree(claimed, vin)) {
        warn('vehicle record VIN does not match the query - card suppressed', claimed);
        return null;
      }
    }
    return vd;
  }

  // A record carrying nothing but a VIN renders as a broken-looking card.
  // Better to show no card at all and let the listings speak for themselves.
  function isRenderableRecord(r) {
    if (!r || typeof r !== 'object') return false;
    if (isArray(r.imageUrls) && r.imageUrls.length) return true;
    if (isArray(r.photos) && r.photos.length) return true;
    if (isArray(r.images) && r.images.length) return true;
    if (r.auctionName || r.auction_house || r.auction) return true;
    if (r.purchasePrice || r.purchase_price || r.sale_price) return true;
    if (r.primaryDamage || r.primary_damage) return true;
    if (r.year || r.make || r.model) return true;
    return false;
  }

  function getPhotoUrl(photo) {
    if (typeof photo === 'string') return photo;
    if (!photo || typeof photo !== 'object') return '';
    return photo.url || photo.src || photo.image_url || photo.imageUrl ||
           photo.full_url || photo.fullUrl || photo.original || '';
  }

  /* ---------------------------------------------------------------
   * Product names
   * ------------------------------------------------------------- */

  // Product titles are written for Google, not for this table: "Bidmotors.bg VIN
  // Removal Tool - Delete & Clean Vehicle History Online | Digital Product".
  // The row already says what we do, so everything after the site's own name is
  // noise that overflows the mobile column. Measured against the live catalogue:
  // without the service-phrase trim, 34 of 53 rows still ended in "VIN Cleaner",
  // "VIN Removal Tool" or similar.
  var SERVICE_TAIL = /\s+(?:&\s+)?(?:vin\s+)?(?:cleaner|cleaning|clean|removal|removing|removed|remove|hiding|hide|deletion|delete)(?:\s+(?:tool|service|package|bundle))?(?:\s+(?:vin|history|records?|report))*$/i;

  /* ---------------------------------------------------------------
   * Delivery time
   *
   * "How long does it take" is the second question every customer asks and
   * the page never answered it.
   *
   * The number comes from the owner: removal at the source takes 12-36h, and
   * Google catches up 1-2 days after that. Both live in section settings, so
   * correcting them is a text edit in the theme editor and they go through
   * Shopify's translation system.
   *
   * Deliberately NOT derived from product titles or handles. Measured against
   * the live catalogue on 31.07: the SEO-rewritten titles contradict the
   * handles on 18 of 28 products, and on the two Google de-indexing products
   * the title promises "12-24h" where the real window is 1-14 and 1-10 days.
   * Guessing from either would mean quoting a delivery time nobody agreed to.
   *
   * Precedence: per-site override -> API -> variant title -> shop default.
   * The variant title outranks the default because when a product really has
   * "Normal up 2 days" and "Fast up to 12 hours" variants, the customer is
   * buying that specific promise.
   * ------------------------------------------------------------- */

  // "24h/7", "24/7" and "24h-7" are availability claims, not delivery windows.
  var AVAILABILITY = /\b24\s*h?\s*[\/-]\s*7\b/g;
  var HOUR = '(?:hours?|hrs?|h)';

  function parseEta(source) {
    if (!source) return null;
    var t = String(source).toLowerCase().replace(AVAILABILITY, ' ');
    var m;
    if ((m = t.match(/(\d+)[\s-]*[-–][\s-]*(\d+)[\s-]*(?:days?)/))) return { lo: +m[1], hi: +m[2], unit: 'd' };
    if ((m = t.match(/(\d+)[\s-]*(?:days?)/)))                      return { lo: +m[1], hi: +m[1], unit: 'd' };

    // Take the LAST match: in "…-24h-7-24h-48h" the window follows the
    // availability token we just stripped.
    var range = t.match(new RegExp('(\\d+)\\s*' + HOUR + '?\\s*[-–]\\s*(\\d+)\\s*' + HOUR + '+', 'g'));
    if (range && range.length) {
      m = range[range.length - 1].match(new RegExp('(\\d+)\\s*' + HOUR + '?\\s*[-–]\\s*(\\d+)'));
      return { lo: +m[1], hi: +m[2], unit: 'h' };
    }
    var one = t.match(new RegExp('(\\d+)\\s*' + HOUR + '+\\b', 'g'));
    if (one && one.length) {
      m = one[one.length - 1].match(/(\d+)/);
      return { lo: +m[1], hi: +m[1], unit: 'h' };
    }
    return null;
  }

  function etaFor(item, meta) {
    var override = CFG.sla && CFG.sla[item.handle];
    if (override) {
      var parsed = parseEta(override);
      // A tidy "1-14 days" is translated; anything else is shown as typed.
      return parsed ? { eta: parsed } : { text: String(override), custom: true };
    }

    var fromApi = meta && meta[item.handle] &&
                  (meta[item.handle].sla || meta[item.handle].sla_hours);

    // A variant name only outranks the shop-wide figure when the product has
    // more than one variant — then "Normal up 2 days" vs "Fast up to 12 hours"
    // is the speed the customer is actually buying. On a single-variant product
    // the name is just an old label ("Normal up to 24h" on a site the owner
    // says takes 12-36h), and one stale label should not make one row disagree
    // with every other row on the page.
    var fromVariant = item.variantCount > 1 ? parseEta(item.variantTitle) : null;

    var e = parseEta(fromApi) || fromVariant || parseEta(CFG.slaDefault) || parseEta(item.variantTitle);
    return e ? { eta: e } : null;
  }

  function etaLabel(info) {
    if (!info) return '';
    if (info.custom) return info.text;
    var e = info.eta;
    if (e.unit === 'd') {
      return e.lo === e.hi ? t('eta_d', { n: e.lo }) : t('eta_d_range', { a: e.lo, b: e.hi });
    }
    return e.lo === e.hi ? t('eta_h', { n: e.lo }) : t('eta_h_range', { a: e.lo, b: e.hi });
  }

  // Widest honest window across a set: earliest anything can be done, latest
  // everything will be. Days and hours are compared on one scale so a 1-14 day
  // item cannot hide behind a 24h one.
  function etaSpan(items) {
    var loH = null, hiH = null, anyDays = false;
    for (var i = 0; i < items.length; i++) {
      var info = items[i].etaInfo;
      if (!info || info.custom) continue;
      var e = info.eta;
      var mult = e.unit === 'd' ? 24 : 1;
      if (e.unit === 'd') anyDays = true;
      var lo = e.lo * mult, hi = e.hi * mult;
      if (loH === null || lo < loH) loH = lo;
      if (hiH === null || hi > hiH) hiH = hi;
    }
    if (loH === null) return '';
    if (anyDays || hiH >= 48) {
      var dLo = Math.max(1, Math.round(loH / 24));
      var dHi = Math.max(1, Math.round(hiH / 24));
      return dLo === dHi ? t('eta_d', { n: dHi }) : t('eta_d_range', { a: dLo, b: dHi });
    }
    return loH === hiH ? t('eta_h', { n: hiH }) : t('eta_h_range', { a: loH, b: hiH });
  }

  function siteLabel(handle, productTitle) {
    if (CFG.siteNames && CFG.siteNames[handle]) return CFG.siteNames[handle];

    var title = String(productTitle || handle || '');
    // "Remove Your Car From X - Digital Product" -> "X"
    title = title.replace(/^\s*remove\s+(your\s+)?(car|vehicle|vin)\s+from\s+/i, '');
    // cut at the first separator that starts the marketing tail
    title = title.split(' – ')[0].split(' - ')[0].split(' | ')[0];
    // drop trailing noise such as "record VIN Photos Delete 24h/7 24h"
    title = title.replace(/\s+(record|records)\b.*$/i, '');
    title = title.replace(/\s+(vin\s+)?(photos?|listing)\s+(delete|removal|remove).*$/i, '');
    title = title.replace(/\s*\b\d+\s*h(\s*\/\s*\d+)?\b.*$/i, '');
    title = title.replace(/\s*\bdigital product\b\.?$/i, '');
    title = title.replace(/^\s+|\s+$/g, '');

    // Peel service phrases off the end, repeatedly — "VIN Hide & Delete" is two.
    // The leading \s+ is load-bearing: it makes a one-word title unstrippable,
    // so "BidFax" and "Autobidmaster" survive intact.
    var guard = 0;
    while (SERVICE_TAIL.test(title) && guard < 6) {
      var shorter = title.replace(SERVICE_TAIL, '').replace(/[\s&\-–|]+$/, '');
      if (shorter.length < 2) break;   // never trim a name away to nothing
      title = shorter;
      guard++;
    }

    return title || handle;
  }

  /* ---------------------------------------------------------------
   * DOM references and state
   * ------------------------------------------------------------- */

  var form, input, button, statusBox, errorBox, results, sticky;
  var rowChecks = {};
  var initialButtonLabel = '';
  var state = {
    vin: '',
    invoice: '',
    busy: false,
    items: [],
    handles: [],
    apiTotalCents: 0
  };

  function init() {
    form = $('vin-form');
    input = $('vin-input');
    button = $('vin-submit-button');
    statusBox = $('vin-status');
    errorBox = $('vin-form-error');
    results = $('display-area');
    sticky = $('vin-sticky');

    if (!input || !results) return;
    if (button) initialButtonLabel = button.textContent || 'Check VIN';

    if (!window.Promise || !window.fetch || !window.JSON) {
      showFatal(t('unsupported'));
      return;
    }

    input.addEventListener('input', onInput);
    input.addEventListener('paste', function () {
      window.setTimeout(onInput, 0);
    });

    if (form) form.addEventListener('submit', onSubmit);
    else if (button) button.addEventListener('click', onSubmit);

    setButtonEnabled(false);
    log('vin-check ready', CFG);
    runFromUrl();
  }

  /* The homepage hero is a plain GET form pointing here, so a customer who
   * typed a VIN on the front page arrives at /pages/vin-check?vin=… — start
   * their check instead of showing them an empty field to fill in twice.
   * Also makes a result shareable and re-runnable from a link. */
  function runFromUrl() {
    var q = String(window.location.search || '');

    /* A claim reference, from the customer's own order confirmation.
     *
     * It is the ONLY thing that entitles a visitor to see removal status for a
     * VIN. Without it the check is anonymous and the answer must be identical
     * for everybody — see the note on `state.claim` where the request is built.
     *
     * Kept separate from state.invoice, which is the id of the check running
     * now and goes onto new cart lines. Mixing the two would stamp somebody
     * else's order id on this order. */
    var c = q.match(/[?&]invoice=([^&#]*)/i);
    if (c) {
      var claim = '';
      try { claim = decodeURIComponent(c[1].replace(/\+/g, ' ')); }
      catch (e) { claim = c[1]; }
      claim = claim.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
      if (claim) state.claim = claim;
    }

    /* The fulfilled-order e-mail's "Clear the rest" button.
     *
     * It says "everything still open", never a list of pages. The e-mail may
     * be days old: a page taken down since it went out must not be sold
     * again, and the scan that is about to run is the only thing that knows.
     * Nothing to go stale, nothing to encode into a link. */
    if (/[?&]cart=open(&|#|$)/i.test(q)) state.autoCart = true;

    var m = q.match(/[?&]vin=([^&#]*)/i);
    if (!m) return;
    var raw = '';
    try { raw = decodeURIComponent(m[1].replace(/\+/g, ' ')); }
    catch (e) { raw = m[1]; }
    if (!raw) return;

    input.value = raw;
    onInput();
    // Only auto-run when the value survived validation — a junk ?vin= in a
    // link must show the same inline message as typing it, not a check.
    if (!vinError(extractVin(input.value) || normalizeVin(input.value))) {
      track('vin_check_from_link', { lang: LANG });
      onSubmit(null);
    }
  }

  function showFatal(text) {
    clear(results);
    results.appendChild(el('div', 'cvf-error', text));
  }

  function setButtonEnabled(on) {
    if (!button) return;
    button.disabled = !on;
  }

  function setError(msg) {
    if (!errorBox) return;
    if (msg) {
      clear(errorBox);
      errorBox.appendChild(document.createTextNode(msg));
      errorBox.hidden = false;
      if (input) input.setAttribute('aria-invalid', 'true');
    } else {
      clear(errorBox);
      errorBox.hidden = true;
      if (input) input.removeAttribute('aria-invalid');
    }
  }

  function normalizeVin(v) {
    return String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17);
  }

  /* ---------------------------------------------------------------
   * VIN extraction from whatever the customer actually pastes.
   * Log audit of 4 982 real inputs: ~36% of rejected entries were
   * recoverable — auction URLs, right-to-left marks, O/0 typos,
   * a VIN inside a sentence, Cyrillic look-alikes.
   * ------------------------------------------------------------- */

  var INVISIBLE = /[​-‏‪-‮⁦-⁩﻿­]/g;

  var CYRILLIC = {
    'А': 'A', 'В': 'B', 'С': 'C', 'Е': 'E', 'Н': 'H', 'К': 'K', 'М': 'M', 'О': 'O',
    'Р': 'P', 'Т': 'T', 'Х': 'X', 'У': 'Y', 'З': '3', 'Ѕ': 'S', 'І': 'I', 'Ј': 'J',
    'а': 'A', 'в': 'B', 'с': 'C', 'е': 'E', 'н': 'H', 'к': 'K', 'м': 'M', 'о': 'O',
    'р': 'P', 'т': 'T', 'х': 'X', 'у': 'Y', 'з': '3', 'ѕ': 'S', 'і': 'I', 'ј': 'J'
  };

  function deHomoglyph(s) {
    return s.replace(/[Ѐ-ӿ]/g, function (ch) {
      return Object.prototype.hasOwnProperty.call(CYRILLIC, ch) ? CYRILLIC[ch] : ch;
    });
  }

  // A VIN never contains I, O or Q — so these are always typos, never data.
  function repairLookalikes(s) {
    return s.replace(/I/g, '1').replace(/O/g, '0').replace(/Q/g, '0');
  }

  function isVinShaped(v) {
    if (v.length !== 17) return false;
    if (/[IOQ]/.test(v)) return false;
    if (!/[0-9]/.test(v)) return false;   // rejects 17-letter words
    if (!/[A-Z]/.test(v)) return false;   // rejects 17-digit ids from URLs
    if (/^(.)\1+$/.test(v)) return false; // rejects 00000000000000000
    return true;
  }

  function digitCount(v) {
    var m = v.match(/[0-9]/g);
    return m ? m.length : 0;
  }

  // `derived` marks the guessing paths: a candidate we produced by repairing
  // I/O/Q or by gluing runs back together. Those paths can turn an ordinary
  // sentence into something VIN-shaped ("please check my car for me" once
  // yielded LEASECHECKMYCARF0), so they carry an extra burden of proof:
  // a real VIN's serial section is numeric, four digits is a low bar for a
  // genuine one and an impassable one for prose.
  function scanRuns(runs, repair, derived) {
    var i, j, win;
    for (i = 0; i < runs.length; i++) {
      for (j = 0; j + 17 <= runs[i].length; j++) {
        win = runs[i].slice(j, j + 17);
        if (repair) win = repairLookalikes(win);
        if (!isVinShaped(win)) continue;
        if ((repair || derived) && digitCount(win) < 4) continue;
        return win;
      }
    }
    return '';
  }

  function extractVin(raw) {
    var s = deHomoglyph(String(raw || '').replace(INVISIBLE, '')).toUpperCase();
    var runs = s.match(/[A-Z0-9]+/g) || [];
    var hit = scanRuns(runs, false, false) || scanRuns(runs, true, false);
    if (hit) return hit;

    // Last resort: the VIN was broken up by spaces or dashes (copy from a PDF
    // or a chat). Deliberately narrow — a VIN split across a few groups is at
    // most a handful of runs and barely longer than 17 characters. Widening
    // this is how the widget starts inventing VINs out of English sentences.
    if (runs.length > 5) return '';
    var joined = runs.join('');
    if (joined.length >= 17 && joined.length <= 22) {
      return scanRuns([joined], false, true) || scanRuns([joined], true, true);
    }
    return '';
  }

  // "Messy" = clearly not someone typing a VIN character by character.
  function looksPasted(raw) {
    var s = String(raw || '');
    return s.length > 17 || /[^A-Za-z0-9]/.test(s);
  }

  function vinError(v) {
    if (!v) return t('err_empty');
    if (v.length !== 17) return t('err_len', { n: v.length });
    if (/[IOQ]/.test(v)) return t('err_ioq');
    // Same guard as onInput — the form can be submitted with Enter while the
    // button is disabled, and a check we never really ran must not come back
    // as "we found nothing". Wording matches the backend's `all-identical`
    // rejection, so the customer sees one message rather than two.
    if (/^(.)\1{16}$/.test(v)) return t('err_not_a_vin');
    return '';
  }

  function onInput() {
    var raw = input.value;
    var found = extractVin(raw);

    if (found) {
      // Bare VIN, pasted auction URL or a VIN inside a sentence — all land here.
      if (raw !== found) input.value = found;
      setError('');
      setButtonEnabled(true);
      return;
    }

    if (looksPasted(raw)) {
      // Leave what they pasted on screen instead of mangling it into gibberish.
      setError(t('err_no_vin_found'));
      setButtonEnabled(false);
      return;
    }

    var normalized = normalizeVin(raw);
    if (raw !== normalized) input.value = normalized;
    setError('');
    // Someone typing character by character: trust the length and the I/O/Q
    // rule rather than the full VIN shape, because a real VIN can look odd.
    // Seventeen identical characters cannot — and letting that through means
    // telling a customer "we found nothing" about a VIN that never existed.
    setButtonEnabled(
      normalized.length === 17 &&
      !/[IOQ]/.test(normalized) &&
      !/^(.)\1{16}$/.test(normalized)
    );
  }

  /* ---------------------------------------------------------------
   * Submit -> API -> products -> render
   * ------------------------------------------------------------- */

  // 400 bodies carry { error, help, reason } with reason in
  // { bad-length, bad-charset, all-identical }. Map to the message the
  // customer already sees for the same mistake typed in the field, so the
  // two paths never contradict each other.
  function reasonMessage(body, vin) {
    var reason = body && body.reason ? String(body.reason) : '';
    if (reason === 'bad-length') return t('err_len', { n: String(vin || '').length });
    if (reason === 'bad-charset') return t('err_ioq');
    if (reason === 'all-identical') return t('err_not_a_vin');
    if (reason === 'no-vin') return t('err_empty');
    // Unknown reason: the backend's own sentence beats a generic one, but only
    // if it reads like a sentence rather than a stack trace.
    if (body && typeof body.error === 'string' && body.error.length < 160) return body.error;
    return t('err_no_vin_found');
  }

  function onSubmit(evt) {
    if (evt && evt.preventDefault) evt.preventDefault();
    if (state.busy) return;

    var raw = String(input.value || '');
    var extracted = extractVin(raw);
    var vin = extracted || normalizeVin(raw);

    /* A pasted link that carries no VIN is its own failure, and it has to win
     * over the length and charset checks.
     *
     * A Copart lot URL normalises to HTTPSWWWCOPARTCOM — seventeen characters,
     * containing an O. So it passed the length check and failed the charset
     * one, and the customer was told their VIN cannot contain the letter O.
     * True sentence, useless advice, and the O came from "copart".
     *
     * onInput already leaves pasted text alone on screen; the same has to hold
     * here, or submitting replaces the link with that mash and the error then
     * refers to something the customer can no longer see. */
    var pasted = !extracted && looksPasted(raw);
    if (!pasted) input.value = vin;

    var err = pasted ? t('err_no_vin_found') : vinError(vin);
    if (err) {
      setError(err);
      /* "We could not find a VIN in that" is the one validation failure where
       * the customer has done nothing wrong and cannot fix it by re-reading the
       * message: a Copart or IAAI lot link genuinely carries no VIN. So this is
       * the moment to put a human in front of them, not a red line. Length and
       * charset errors are self-explanatory and get no extra block. */
      clear(results);
      if (err === t('err_no_vin_found')) appendHelp(results);
      track('vin_check_error', { error_type: 'validation', lang: LANG });
      if (input.focus) input.focus();
      return;
    }

    setError('');
    state.vin = vin;
    state.busy = true;
    setButtonEnabled(false);
    if (button) {
      clear(button);
      button.appendChild(document.createTextNode(t('checking')));
    }

    showStatus(vin);
    showSkeleton();
    track('vin_check_submitted', { lang: LANG });

    var startedAt = new Date().getTime();

    fetchWithTimeout(CFG.apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      /* `invoice` is sent only when the visitor arrived with a claim reference
       * from their own order. The checker must return `site_status` only for a
       * valid invoice+VIN pair, and never for a bare VIN.
       *
       * Anything else leaks. A stranger typing a VIN and being told "removed
       * 3 Aug" learns that this car's owner paid to take records down — which
       * is the precise harm the customer bought protection from, published by
       * us, about them. Competitors watching the queue is the smaller half of
       * that problem. */
      body: JSON.stringify(state.claim ? { vin: vin, invoice: state.claim } : { vin: vin })
    }, REQUEST_TIMEOUT_MS)
      .then(function (response) {
        return response.text().then(function (text) {
          if (!response.ok) {
            // The backend validates the VIN too, and on 400 it says exactly
            // what was wrong. Our own guards should have caught all three
            // before the request went out — so reaching here means the two
            // layers disagree, which is worth seeing in the console.
            if (response.status === 400) {
              var body = null;
              try { body = JSON.parse(text); } catch (e) {}
              var err = new Error('bad_vin');
              err.inline = reasonMessage(body, vin);
              warn('backend rejected a VIN our own validation accepted', body);
              throw err;
            }
            throw new Error('http_' + response.status);
          }
          try { return JSON.parse(text); }
          catch (e) { throw new Error('bad_json'); }
        });
      })
      .then(function (json) {
        var apiData = getApiData(json, vin);
        state.invoice = apiData.invoice_number || '';
        state.handles = extractHandles(apiData);
        log('api ok', apiData);
        checkPriceDrift(apiData);
        // Real milestone: the search is finished and we know the true count.
        progressTo('matched', state.handles.length);
        return buildItems(state.handles, apiData).then(function (items) {
          // The customer may already hold some of these lines — from an earlier
          // check, or from following the cart's own "add the rest" link back
          // here. Adding them again charges twice for the same page, so they
          // are shown as already in the cart and cannot be ticked. A cart read
          // that fails simply marks nothing.
          return cartVariants().then(function () { return items; });
        }).then(function (items) {
          // The size of the find, stamped before anything is marked as already
          // held. `totalOf` skips non-selectable rows, so reading it after
          // markAlreadyInCart under-reports the check on every repeat visit —
          // and this number ends up on the order and in the customer's email.
          state.foundCount = items.length;
          state.foundCents = totalOf(items);
          markAlreadyInCart(items);
          state.items = items;
          progressTo('done');
          renderResults(apiData, items);
          autoCartFromLink();
          track('vin_check_result', {
            sites_found: items.length,
            total_value: totalOf(items) / 100,
            has_vehicle_data: usableVehicle(apiData, vin) ? 1 : 0,
            // Never shown to the customer. It is the only thing that separates
            // "this car has no auction record" (EMPTY) from "our lookup failed"
            // — a distinction neither side could measure before.
            vehicle_status: String(apiData.vehicle_status || 'UNKNOWN'),
            search_status: String(apiData.search_status || apiData.searchStatus || 'OK').toUpperCase(),
            urls_returned: countUrls(items),
            duration_ms: new Date().getTime() - startedAt,
            lang: LANG
          });
        });
      })
      ['catch'](function (error) {
        warn('check failed', error);
        if (error && error.inline) {
          // A rejected VIN belongs next to the field the customer can fix,
          // not in a red panel where the results should be.
          stopProgress();
          clear(results);
          results.setAttribute('aria-busy', 'false');
          setError(error.inline);
          appendHelp(results);
          if (input && input.focus) input.focus();
        } else {
          renderError(String(error && error.message) === 'http_429' ? 'rate' : '');
        }
        track('vin_check_error', { error_type: String(error && error.message || 'unknown'), lang: LANG });
      })
      .then(finish, finish);
  }

  function finish() {
    state.busy = false;
    if (results) results.setAttribute('aria-busy', 'false');
    if (button) {
      clear(button);
      button.appendChild(document.createTextNode(initialButtonLabel));
    }
    setButtonEnabled(normalizeVin(input.value).length === 17);
  }

  function showStatus(vin) {
    if (!statusBox) return;
    var now = new Date();
    clear(statusBox);
    statusBox.appendChild(el('span', 'vin-status__label', t('checking_vin') + ': '));
    statusBox.appendChild(el('span', 'vin-status__vin', vin));
    statusBox.appendChild(el('span', 'vin-status__time', ' · ' + t('checked_at', {
      date: now.toLocaleDateString(),
      time: now.toLocaleTimeString()
    })));
    statusBox.hidden = false;
  }

  var slowTimer = null;

  /* ---------------------------------------------------------------
   * Waiting state
   *
   * Three shimmer bars told the customer nothing: it looked like the page
   * had stalled rather than like work was happening. A check takes ~3s warm
   * and up to 30s cold, which is long enough to abandon.
   *
   * So: name the work. Two of the three steps advance on REAL events — the
   * API answering, and the product lookups finishing — and the second one
   * fills in the true number of sites the moment we know it. Only the motion
   * between those events is time-based, which is what a progress bar is.
   * The bar deliberately never reaches 100% until the work is actually done,
   * so it cannot sit full while the customer waits.
   * ------------------------------------------------------------- */

  var progress = null;

  function showSkeleton() {
    clear(results);
    results.setAttribute('aria-busy', 'true');
    stopProgress();

    var box = el('div', 'cvf-loading');

    var head = el('div', 'cvf-loading__head');
    head.appendChild(el('span', 'cvf-loading__title', t('scanning')));
    var pct = el('span', 'cvf-loading__pct', '0%');
    head.appendChild(pct);
    box.appendChild(head);

    var track = el('div', 'cvf-bar');
    track.setAttribute('role', 'progressbar');
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', '100');
    track.setAttribute('aria-valuenow', '0');
    track.setAttribute('aria-label', t('scanning'));
    var fill = el('div', 'cvf-bar__fill');
    track.appendChild(fill);
    box.appendChild(track);

    var list = el('ol', 'cvf-steps');
    var steps = [];
    var labels = [t('step_search'), t('step_match'), t('step_price')];
    for (var i = 0; i < labels.length; i++) {
      var li = el('li', 'cvf-step');
      li.appendChild(el('span', 'cvf-step__mark'));
      li.appendChild(el('span', 'cvf-step__text', labels[i]));
      list.appendChild(li);
      steps.push(li);
    }
    box.appendChild(list);

    var note = el('p', 'cvf-loading__note');
    note.hidden = true;
    box.appendChild(note);

    results.appendChild(box);

    progress = {
      value: 0, ceiling: 55, fill: fill, pct: pct, track: track,
      steps: steps, note: note, stage: 0, timer: null
    };
    setStep(0, 'active');

    progress.timer = window.setInterval(function () {
      if (!progress) return;
      // Ease towards the current ceiling: fast at first, slower as it closes
      // in, and never past it. Real milestones are what raise the ceiling.
      progress.value += (progress.ceiling - progress.value) * 0.08;
      paintProgress();
    }, 120);

    if (slowTimer) window.clearTimeout(slowTimer);
    slowTimer = window.setTimeout(function () {
      if (!progress) return;
      clear(progress.note);
      progress.note.appendChild(document.createTextNode(t('scanning_slow')));
      progress.note.hidden = false;
    }, SLOW_NOTICE_MS);

    scrollToResults();
  }

  function paintProgress() {
    if (!progress) return;
    var v = Math.max(0, Math.min(100, Math.round(progress.value)));
    progress.fill.style.width = v + '%';
    progress.track.setAttribute('aria-valuenow', String(v));
    clear(progress.pct);
    progress.pct.appendChild(document.createTextNode(v + '%'));
  }

  function setStep(i, state) {
    if (!progress || !progress.steps[i]) return;
    progress.steps[i].className = 'cvf-step cvf-step--' + state;
  }

  // Called when a real milestone lands. `sites` is filled in on 'matched'.
  function progressTo(stage, sites) {
    if (!progress) return;
    if (stage === 'matched') {
      setStep(0, 'done');
      if (typeof sites === 'number') {
        var text = progress.steps[1].getElementsByTagName('span')[1];
        clear(text);
        text.appendChild(document.createTextNode(t('step_match_n', { n: sites })));
      }
      setStep(1, 'done');
      setStep(2, 'active');
      progress.ceiling = 88;
      progress.value = Math.max(progress.value, 70);
      paintProgress();
    } else if (stage === 'done') {
      setStep(2, 'done');
      progress.ceiling = 100;
      progress.value = 100;
      paintProgress();
    }
  }

  function stopProgress() {
    if (progress && progress.timer) window.clearInterval(progress.timer);
    progress = null;
  }

  function scrollToResults() {
    try {
      if (results.scrollIntoView) results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
      try { results.scrollIntoView(true); } catch (e2) {}
    }
  }

  /* ---------------------------------------------------------------
   * Products - fetched in parallel, rendered in a deterministic order
   * ------------------------------------------------------------- */

  function fetchProduct(handle, index) {
    var url = routesRoot() + 'products/' + encodeURIComponent(handle) + '.js';
    return fetchWithTimeout(url, { headers: { 'Accept': 'application/json' } }, 15000)
      .then(function (r) {
        if (!r.ok) throw new Error('product_http_' + r.status);
        return r.json();
      })
      .then(function (product) {
        var variant = pickVariant(product);
        return {
          handle: handle,
          index: index,
          available: !!(variant && variant.available !== false),
          title: product.title || handle,
          name: siteLabel(handle, product.title),
          url: routesRoot() + 'products/' + encodeURIComponent(handle),
          image: safeUrl(product.featured_image || (product.images && product.images[0]) || ''),
          variantId: variant ? variant.id : null,
          variantTitle: (variant && variant.title && variant.title !== 'Default Title') ? variant.title : '',
          variantCount: (product && product.variants && product.variants.length) || 0,
          price: variant ? Number(variant.price) : 0,
          compareAt: variant && variant.compare_at_price ? Number(variant.compare_at_price) : 0,
          missing: false
        };
      })
      ['catch'](function (e) {
        warn('product fetch failed: ' + handle, e);
        return {
          handle: handle, index: index, available: false, missing: true,
          title: handle, name: siteLabel(handle, ''), url: '', image: '',
          variantId: null, variantTitle: '', price: 0, compareAt: 0
        };
      });
  }

  function pickVariant(product) {
    var variants = (product && product.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && variants[i].available) return variants[i];
    }
    return variants[0] || null;
  }

  /* The catalogue's own names, from GET /sites.
   *
   * Until this existed the row name was carved out of a product title written
   * for search engines — a regex fighting "Bidmotors.bg VIN Removal Tool -
   * Delete & Clean Vehicle History Online | Digital Product". The checker now
   * publishes the domain per site_id, which is both authoritative and the thing
   * the customer would actually type into a browser. Cached for the page's
   * lifetime; a failure here costs nothing because the old path still works. */
  var siteDirectory = null;

  /* The same GET /sites rows, kept whole rather than flattened into a name map.
     `site_id` in that feed IS the Shopify product handle — which is what makes
     the "add a site" picker below possible without a second catalogue. */
  var siteCatalog = [];

  function loadSiteNames() {
    if (siteDirectory) return siteDirectory;
    var base = String(CFG.apiUrl || '').replace(/\/check-vin\/?$/i, '');
    if (!base) return (siteDirectory = Promise.resolve({}));

    siteDirectory = fetchWithTimeout(base + '/sites', { headers: { 'Accept': 'application/json' } }, 8000)
      .then(function (r) {
        if (!r.ok) throw new Error('sites_http_' + r.status);
        return r.json();
      })
      .then(function (json) {
        var rows = (json && isArray(json.sites)) ? json.sites : [];
        var map = {}, keep = [];
        for (var i = 0; i < rows.length; i++) {
          var row = rows[i];
          if (!row || !row.site_id) continue;
          var name = row.display_name || row.domain || '';
          if (name) map[row.site_id] = String(name);
          // `active` and `public` are the catalogue's own switches. A row that
          // fails either is one we do not currently sell, so it must not turn
          // up in the picker.
          if (row.active !== false && row.public !== false) keep.push(row);
        }
        siteCatalog = keep;
        log('site directory', rows.length);
        return map;
      })
      ['catch'](function (e) { warn('site directory unavailable', e); return {}; });

    return siteDirectory;
  }

  function buildItems(handles, apiData) {
    var jobs = [loadSiteNames()];
    for (var i = 0; i < handles.length; i++) jobs.push(fetchProduct(handles[i], i));

    return Promise.all(jobs).then(function (all) {
      var names = all[0] || {};
      var items = all.slice(1);
      for (var n = 0; n < items.length; n++) {
        // Merchant override still wins; then the catalogue; then the title.
        if (CFG.siteNames && CFG.siteNames[items[n].handle]) continue;
        if (names[items[n].handle]) items[n].name = names[items[n].handle];
      }
      return items;
    }).then(function (items) {
      var apiPriority = isArray(apiData.priority) ? apiData.priority : [];
      var meta = apiData.priority_meta || {};
      var statuses = apiData.site_status || {};

      var matches = matchesOf(apiData);

      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        it.reasons = (meta[it.handle] && isArray(meta[it.handle].reasons)) ? meta[it.handle].reasons : [];
        applyMatch(it, matches[it.handle]);
        it.status = statuses[it.handle] || null;
        it.done = !!(it.status && (it.status.state === 'removed' || it.status.state === 'verified'));
        it.pending = !!(it.status && (it.status.state === 'ordered' || it.status.state === 'in_progress'));
        it.rank = rankOf(it.handle, apiPriority);
        it.selectable = !!(it.available && !it.done && !it.pending && !it.missing && it.variantId);
        it.etaInfo = etaFor(it, meta);
      }

      /* Order matters here, and it is not obvious.
       *
       * applyBundles refuses to bundle anything the customer could not have
       * bought separately — it tests `selectable`. A line already sitting in
       * the cart is exactly that case: substituting a bundle over it would
       * charge a second time for a page already paid for, inside a line item
       * whose name does not even mention it.
       *
       * So the cart has to be known BEFORE bundles are considered, not after
       * the list is built. Marking again further down catches the other
       * direction — a bundle line that is itself already in the cart. */
      return cartVariants().then(function () {
        markAlreadyInCart(items);
        return applyBundles(items);
      }).then(function (list) {
        list.sort(function (a, b) {
          if (a.rank !== b.rank) return a.rank - b.rank;
          if (a.price !== b.price) return a.price - b.price;
          return a.index - b.index;
        });
        return list;
      });
    });
  }

  /* ---------------------------------------------------------------
   * Bundles
   *
   * When a VIN turns up on plc.ua AND plc.auction the page used to quote
   * $70 + $70 while a $120 bundle covering both sat in the catalogue. It sold
   * ten orders in sixty days entirely on its own, without the checker ever
   * offering it — customers were finding it themselves and the ones who did
   * not simply overpaid by $20.
   *
   * Configured in the section settings rather than hard-coded, so a new bundle
   * is a text change in the theme editor, not a deploy.
   * ------------------------------------------------------------- */

  function bundleDefs() {
    if (!isArray(CFG.bundles)) return [];
    var out = [];
    for (var i = 0; i < CFG.bundles.length; i++) {
      var b = CFG.bundles[i];
      if (b && b.handle && isArray(b.members) && b.members.length > 1) out.push(b);
    }
    return out;
  }

  function applyBundles(items) {
    var defs = bundleDefs();
    if (!defs.length) return Promise.resolve(items);

    var byHandle = {};
    for (var i = 0; i < items.length; i++) byHandle[items[i].handle] = items[i];

    var candidates = [];
    for (var d = 0; d < defs.length; d++) {
      var def = defs[d];
      var members = [], ok = true;
      for (var m = 0; m < def.members.length; m++) {
        var it = byHandle[def.members[m]];
        // Only bundle things the customer could actually have bought separately.
        // A site already removed for this VIN must not be silently re-sold
        // inside a bundle.
        if (!it || !it.selectable) { ok = false; break; }
        members.push(it);
      }
      if (ok) candidates.push({ def: def, members: members });
    }
    if (!candidates.length) return Promise.resolve(items);

    var jobs = [];
    for (var c = 0; c < candidates.length; c++) {
      jobs.push(fetchProduct(candidates[c].def.handle, 10000 + c));
    }

    return Promise.all(jobs).then(function (bundles) {
      var drop = {};
      var add = [];

      for (var k = 0; k < bundles.length; k++) {
        var bundle = bundles[k];
        var members = candidates[k].members;
        if (bundle.missing || !bundle.available || !bundle.variantId) continue;

        var separate = 0, bestRank = 500, lowIndex = 1e9, names = [], memberHandles = [];
        for (var n = 0; n < members.length; n++) {
          separate += members[n].price;
          if (members[n].rank < bestRank) bestRank = members[n].rank;
          if (members[n].index < lowIndex) lowIndex = members[n].index;
          names.push(members[n].name);
          memberHandles.push(members[n].handle);
        }
        // Never "help" the customer into a worse price. If the bundle is not
        // actually cheaper, leave the individual rows alone.
        if (bundle.price >= separate) {
          log('bundle skipped, not cheaper', bundle.handle, bundle.price, separate);
          continue;
        }

        for (var q = 0; q < members.length; q++) drop[members[q].handle] = true;

        bundle.isBundle = true;
        bundle.covers = names;
        bundle.coverHandles = memberHandles;
        bundle.saving = separate - bundle.price;
        bundle.separatePrice = separate;
        bundle.rank = bestRank;
        bundle.index = lowIndex;
        bundle.reasons = [];
        bundle.urls = [];
        bundle.status = null;
        bundle.done = false;
        bundle.pending = false;
        bundle.selectable = true;
        bundle.etaInfo = etaFor(bundle, {});
        // The order still has to say which sites were paid for, so carry the
        // members' VIN-bearing URLs across into the bundle line.
        for (var u = 0; u < members.length; u++) {
          if (members[u].urls) {
            for (var v = 0; v < members[u].urls.length; v++) {
              if (bundle.urls.indexOf(members[u].urls[v]) === -1) bundle.urls.push(members[u].urls[v]);
            }
          }
        }
        add.push(bundle);
        track('bundle_offered', {
          bundle: bundle.handle,
          saving: bundle.saving / 100,
          currency: activeCurrency()
        });
      }

      if (!add.length) return items;

      var out = [];
      for (var z = 0; z < items.length; z++) {
        if (!drop[items[z].handle]) out.push(items[z]);
      }
      return out.concat(add);
    })['catch'](function (e) {
      // A bundle is an upsell, never a dependency: if the lookup fails the
      // customer still gets the individual rows.
      warn('bundle lookup failed', e);
      return items;
    });
  }

  function rankOf(handle, apiPriority) {
    var i;
    if (apiPriority && apiPriority.length) {
      i = apiPriority.indexOf(handle);
      if (i > -1) return i;
      return 500;
    }
    i = CFG.priority.indexOf(handle);
    return i > -1 ? i : 500;
  }


  /* ---------------------------------------------------------------
   * Found pages: the exact URLs where the VIN was seen. This is what
   * turns "we remove from BidFax" into "we remove THIS page", and it
   * is what the customer later checks to see the removal happened.
   * ------------------------------------------------------------- */

  function matchesOf(apiData) {
    var m = apiData.matches || apiData.found_urls || apiData.site_matches;
    return (m && typeof m === 'object') ? m : {};
  }

  function applyMatch(item, entry) {
    item.urls = [];
    if (!entry) return;

    // A site can match on a "similar cars" carousel that belongs to a DIFFERENT
    // vehicle. The checker still lists those sites, deliberately with an empty
    // `urls`, so we get the badges without a link. An empty array is therefore
    // an answer, not missing data: never fall through to another field for one.
    var raw = [];
    if (isArray(entry)) raw = entry;
    else if (typeof entry === 'string') raw = [entry];
    else if (isArray(entry.urls)) raw = entry.urls;
    else if (entry.url) raw = [entry.url];

    for (var i = 0; i < raw.length; i++) {
      var u = safeUrl(typeof raw[i] === 'string' ? raw[i] : (raw[i] && raw[i].url));
      if (u && item.urls.indexOf(u) === -1) item.urls.push(u);
    }

    if (entry && typeof entry === 'object' && !isArray(entry)) {
      if (typeof entry.serp_rank === 'number') item.serpRank = entry.serp_rank;
      if (entry.has_photos) item.hasPhotos = true;
    }
  }

  function countUrls(items) {
    var n = 0;
    for (var i = 0; i < items.length; i++) n += (items[i].urls && items[i].urls.length) || 0;
    return n;
  }

  // Host plus a trimmed path — a full auction URL is unreadable in a table row.
  function shortUrl(u) {
    var s = String(u || '').replace(/^https?:\/\//i, '').replace(/\/$/, '');
    if (s.length <= 52) return s;
    var slash = s.indexOf('/');
    var host = slash > -1 ? s.slice(0, slash) : s;
    var tail = s.slice(-24);
    return host + '/…' + tail;
  }

  function totalOf(items) {
    var sum = 0;
    for (var i = 0; i < items.length; i++) {
      if (items[i].selectable) sum += items[i].price;
    }
    return sum;
  }

  /* ---------------------------------------------------------------
   * Rendering
   * ------------------------------------------------------------- */

  // The contact block lives in the section (merchant-editable rich text) and is
  // cloned in, so no state ever leaves the customer without a way to reach a human.
  // Clones a hidden block of merchant-editable rich text out of the section.
  // Editorial copy lives in section settings rather than in this file so it
  // goes through Shopify's own translation system instead of needing a deploy
  // for every language.
  function cloneBlock(id, host, cls) {
    var src = document.getElementById(id);
    if (!src || !src.childNodes.length) return false;
    var box = el('div', cls);
    var kids = src.childNodes;
    for (var i = 0; i < kids.length; i++) box.appendChild(kids[i].cloneNode(true));
    host.appendChild(box);
    return true;
  }

  function appendTrust(host) {
    cloneBlock('vin-trust', host, 'cvf-trust');
  }

  function appendHelp(host) {
    var src = document.getElementById('vin-help');
    if (!src) return;
    var box = el('div', 'cvf-help');
    box.appendChild(el('p', 'cvf-help__title', t('help_title')));
    var body = el('div', 'cvf-help__body');
    var kids = src.childNodes;
    for (var i = 0; i < kids.length; i++) body.appendChild(kids[i].cloneNode(true));
    box.appendChild(body);
    host.appendChild(box);
  }

  /* ---------------------------------------------------------------
   * "Site not listed? Add it" — the catalogue picker
   *
   * The scan reports what it can see. It cannot see a page Google has not
   * indexed yet, or one the customer found by hand on a forum — and those
   * customers had nowhere to go but e-mail, which usually meant they went
   * nowhere. This opens the same catalogue the scan draws from and lets them
   * pick from it, into the same order.
   *
   * GET /sites is that catalogue, and its `site_id` IS the Shopify handle, so
   * no second list has to be kept in step with anything.
   *
   * Prices are not in that feed, so each row loads its own product on demand.
   * Sixty-two product fetches on open would cost more than the feature is
   * worth; a dozen at a time, and more as the list scrolls, costs nothing.
   *
   * Anything the scan already found for this VIN and anything already held is
   * filtered out — including the members hiding inside a bundle line. A picker
   * that offers what the page above already lists is how a customer ends up
   * paying twice for one page.
   * ------------------------------------------------------------- */

  /* One request for the whole catalogue's prices, instead of one per row.
   *
   * /products/<handle>.js carries the entire product — including a description
   * written for search engines — so a dozen rows cost hundreds of kilobytes and
   * several seconds, and the picker sat on "…" while they arrived.
   *
   * templates/collection.cvn-prices.liquid returns the same facts in a few
   * kilobytes: price, variant, availability. Shopify caps a page at 50 products,
   * so the response says how many pages there are and we follow them.
   *
   * If the template is missing the response is ordinary HTML, JSON.parse throws,
   * and every row falls back to the per-product fetch below. Slower, but never
   * broken. */
  var priceMap = null;
  var priceMapPromise = null;
  var PRICE_PAGE_CAP = 8;

  /* Shopify's CDN caches this response, and a price you changed in the admin
     must not keep selling at the old figure. The bucket changes every five
     minutes, so the worst case is five minutes stale — while everyone inside
     the same window still shares one URL and one cached response.

     Nothing here decides what the customer is charged: that comes from the
     variant at checkout. A stale map can only show a price that differs from
     the real one, which is why five minutes is enough and one second would be
     waste. */
  function priceBucket() {
    return Math.floor((new Date().getTime()) / 300000);
  }

  function fetchPricePage(page, acc) {
    var url = routesRoot() + 'collections/all?view=cvn-prices&page=' + page +
              '&t=' + priceBucket();
    return fetchWithTimeout(url, { headers: { 'Accept': 'application/json' } }, 10000)
      .then(function (r) {
        if (!r.ok) throw new Error('pricemap_http_' + r.status);
        return r.text();
      })
      .then(function (txt) {
        var json = JSON.parse(txt);
        var items = json.items || {}, k;
        for (k in items) {
          if (Object.prototype.hasOwnProperty.call(items, k)) acc[k] = items[k];
        }
        var pages = Number(json.pages) || 1;
        var here = Number(json.page) || page;
        if (here < pages && page < PRICE_PAGE_CAP) return fetchPricePage(page + 1, acc);
        return acc;
      });
  }

  function loadPriceMap() {
    if (priceMapPromise) return priceMapPromise;
    priceMapPromise = fetchPricePage(1, {})
      .then(function (map) {
        priceMap = map;
        log('price map', Object.keys ? Object.keys(map).length : '?');
        return map;
      })['catch'](function (e) {
        warn('price map unavailable, falling back to per-product fetch', e);
        priceMap = {};
        return {};
      });
    return priceMapPromise;
  }

  // A catalogue row shaped like a scan row, so cartPayload cannot tell them
  // apart and the order carries the same properties either way.
  function itemFromPriceMap(handle, label) {
    var row = priceMap && priceMap[handle];
    if (!row || !row.v || row.a === false) return null;
    return {
      handle: handle,
      name: label,
      title: label,
      price: Number(row.p) || 0,
      variantId: row.v,
      variantTitle: row.t || '',
      variantCount: Number(row.n) || 1,
      available: true,
      missing: false,
      selectable: true,
      urls: []
    };
  }

  var productCache = {};

  function isEmptyMap(o) {
    var k;
    for (k in o) if (Object.prototype.hasOwnProperty.call(o, k)) return false;
    return true;
  }

  function catalogueProduct(handle) {
    if (!productCache[handle]) productCache[handle] = fetchProduct(handle, 0);
    return productCache[handle];
  }

  function alreadyCovered() {
    var seen = {}, items = state.items || [], i, j;
    for (i = 0; i < items.length; i++) {
      seen[items[i].handle] = true;
      var covers = items[i].coverHandles;
      if (covers) for (j = 0; j < covers.length; j++) seen[covers[j]] = true;
    }
    var held = state.inCart || {}, k;
    for (k in held) {
      if (Object.prototype.hasOwnProperty.call(held, k) && held[k]) seen[k] = true;
    }
    return seen;
  }

  function catalogueRows(query) {
    var seen = alreadyCovered(), out = [], i;
    var q = String(query || '').toLowerCase().replace(/^\s+|\s+$/g, '');
    for (i = 0; i < siteCatalog.length; i++) {
      var row = siteCatalog[i];
      var id = row && row.site_id;
      if (!id || seen[id]) continue;
      var label = String(row.display_name || row.domain || id);
      // Match the handle too: a customer pasting a URL fragment should still
      // land on the right row.
      if (q && label.toLowerCase().indexOf(q) === -1 && id.toLowerCase().indexOf(q) === -1) continue;
      out.push({ id: id, label: label });
    }
    out.sort(function (a, b) {
      var x = a.label.toLowerCase(), y = b.label.toLowerCase();
      return x < y ? -1 : x > y ? 1 : 0;
    });
    return out;
  }

  function appendAddSites(host) {
    // Warm the catalogue and its prices now, while the customer is still
    // reading the list. By the time the button is clicked the picker has
    // everything and opens fully priced.
    loadSiteNames();
    loadPriceMap();

    var wrap = el('div', 'cvf-addsite');
    var btn = el('button', 'cvf-addsite__btn', t('add_site_open'));
    btn.type = 'button';
    btn.addEventListener('click', function () { openAddSites(btn); });
    wrap.appendChild(btn);
    host.appendChild(wrap);
  }

  function confirmAdded(pages) {
    var box = el('div', 'cvf-notice cvf-notice--added');
    box.appendChild(el('span', null, t('add_site_added', { n: pages })));
    var go = el('button', 'cvf-notice__go', t('add_site_go'));
    go.type = 'button';
    go.addEventListener('click', goToCart);
    box.appendChild(go);
    results.appendChild(box);
    if (box.scrollIntoView) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function openAddSites(trigger) {
    var CHUNK = 12;
    var picked = {};
    var rows = [];
    var loaded = 0;
    var prevOverflow = document.body.style.overflow;

    var overlay = el('div', 'cvf-modal');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', t('add_site_title'));

    var panel = el('div', 'cvf-modal__panel');

    var head = el('div', 'cvf-modal__head');
    head.appendChild(el('h2', 'cvf-modal__title', t('add_site_title')));
    var xBtn = el('button', 'cvf-modal__x', '×');
    xBtn.type = 'button';
    xBtn.setAttribute('aria-label', t('add_site_close'));
    head.appendChild(xBtn);
    panel.appendChild(head);

    panel.appendChild(el('p', 'cvf-modal__intro', t('add_site_intro')));

    var search = document.createElement('input');
    search.type = 'search';
    search.className = 'cvf-modal__search';
    search.placeholder = t('add_site_search');
    search.setAttribute('aria-label', t('add_site_search'));
    search.autocomplete = 'off';
    panel.appendChild(search);

    var list = el('div', 'cvf-modal__list');
    panel.appendChild(list);

    var foot = el('div', 'cvf-modal__foot');
    var addBtn = el('button', 'cvf-modal__add');
    addBtn.type = 'button';
    var cancelBtn = el('button', 'cvf-modal__cancel', t('add_site_cancel'));
    cancelBtn.type = 'button';
    foot.appendChild(addBtn);
    foot.appendChild(cancelBtn);
    panel.appendChild(foot);

    overlay.appendChild(panel);

    function close() {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      if (trigger && trigger.focus) trigger.focus();
    }

    function onKey(e) {
      if (e.key === 'Escape' || e.keyCode === 27) { e.preventDefault(); close(); }
    }

    function syncFoot() {
      var pages = 0, cents = 0, k;
      for (k in picked) {
        if (!Object.prototype.hasOwnProperty.call(picked, k)) continue;
        pages += siteSpan(picked[k]);
        cents += picked[k].price;
      }
      addBtn.disabled = !pages;
      clear(addBtn);
      addBtn.appendChild(document.createTextNode(
        t('add_site_add', { n: pages, price: money(cents) })));
    }

    function apply(entry, p) {
      entry.product = p;
      entry.price.textContent = money(p.price);
      entry.meta.textContent = etaLabel(etaFor(p, {}));
      entry.box.disabled = false;
    }

    function unavailable(entry) {
      entry.meta.textContent = t('add_site_soon');
      entry.price.textContent = '';
      if (entry.node.className.indexOf('cvf-msite--off') === -1) {
        entry.node.className += ' cvf-msite--off';
      }
    }

    function hydrate(entry) {
      if (entry.asked) return;

      // The common path: the map is already in memory, so the row is priced
      // in the same tick it is drawn. No request, no waiting dots.
      if (priceMap && priceMap[entry.id] !== undefined) {
        entry.asked = true;
        var mapped = itemFromPriceMap(entry.id, entry.label);
        if (mapped) apply(entry, mapped); else unavailable(entry);
        return;
      }
      // Fallback only: the price-map template is missing or failed.
      entry.asked = true;
      catalogueProduct(entry.id).then(function (p) {
        if (!p || p.missing || !p.variantId || p.available === false) {
          unavailable(entry);
          return;
        }
        // Shaped like a scan row so cartPayload treats it identically and the
        // order carries the same _checker_* properties as everything else.
        p.urls = [];
        p.selectable = true;
        apply(entry, p);
      });
    }

    function rowNode(entry) {
      var row = el('label', 'cvf-msite');
      var box = document.createElement('input');
      box.type = 'checkbox';
      box.className = 'cvf-msite__check';
      box.disabled = true;
      row.appendChild(box);

      var body = el('div', 'cvf-msite__body');
      body.appendChild(el('span', 'cvf-msite__name', entry.label));
      var meta = el('span', 'cvf-msite__meta', '…');
      body.appendChild(meta);
      row.appendChild(body);

      var price = el('span', 'cvf-msite__price', '…');
      row.appendChild(price);

      entry.node = row; entry.box = box; entry.meta = meta; entry.price = price;

      // Searching rebuilds the list, so a row that was ticked before the search
      // has to come back ticked — and already priced, without asking again.
      if (picked[entry.id]) {
        entry.product = picked[entry.id];
        entry.asked = true;
        box.checked = true;
        box.disabled = false;
        price.textContent = money(entry.product.price);
        meta.textContent = etaLabel(etaFor(entry.product, {}));
      }

      box.addEventListener('change', function () {
        if (box.checked && entry.product) picked[entry.id] = entry.product;
        else delete picked[entry.id];
        syncFoot();
      });

      return row;
    }

    function loadMore() {
      // Reading a map costs nothing, so chunking only applies to the fallback
      // path where every row is a separate request.
      var haveMap = priceMap && !isEmptyMap(priceMap);
      var end = haveMap ? rows.length : Math.min(rows.length, loaded + CHUNK);
      for (; loaded < end; loaded++) hydrate(rows[loaded]);
    }

    function render() {
      clear(list);
      loaded = 0;
      rows = catalogueRows(search.value);

      if (!siteCatalog.length) {
        list.appendChild(el('p', 'cvf-modal__note', t('add_site_err')));
        return;
      }
      if (!rows.length) {
        list.appendChild(el('p', 'cvf-modal__note',
          search.value ? t('add_site_none') : t('add_site_covered')));
        return;
      }
      for (var i = 0; i < rows.length; i++) list.appendChild(rowNode(rows[i]));
      loadMore();
    }

    xBtn.addEventListener('click', close);
    cancelBtn.addEventListener('click', close);
    overlay.addEventListener('mousedown', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', onKey, true);
    search.addEventListener('input', render);
    list.addEventListener('scroll', function () {
      if (list.scrollTop + list.clientHeight >= list.scrollHeight - 160) loadMore();
    });

    addBtn.addEventListener('click', function () {
      var chosen = [], k;
      for (k in picked) {
        if (Object.prototype.hasOwnProperty.call(picked, k)) chosen.push(picked[k]);
      }
      if (!chosen.length) return;
      addBtn.disabled = true;

      var pages = siteCount(chosen);
      track('add_to_cart', {
        value: totalOf(chosen) / 100,
        currency: activeCurrency(),
        items_count: chosen.length,
        manual_pick: true
      });

      postToCart(chosen).then(stampCart).then(function () {
        close();
        confirmAdded(pages);
      })['catch'](function (e) {
        warn('manual site add failed', e);
        addBtn.disabled = false;
        list.insertBefore(el('p', 'cvf-modal__note', t('err_cart')), list.firstChild);
      });
    });

    document.body.style.overflow = 'hidden';
    document.body.appendChild(overlay);
    syncFoot();
    list.appendChild(el('p', 'cvf-modal__note', '…'));

    // Both are cached after the first call — and both were warmed when the
    // button was drawn — so this normally resolves in the same tick.
    var ready = (window.Promise && Promise.all)
      ? Promise.all([loadSiteNames(), loadPriceMap()])
      : loadSiteNames();
    ready.then(render, render);

    track('view_add_sites', { lang: LANG });
    if (search.focus) { try { search.focus(); } catch (e) {} }
  }

  function renderResults(apiData, items) {
    if (slowTimer) window.clearTimeout(slowTimer);
    stopProgress();
    clear(results);
    rowChecks = {};
    state.scopes = null;
    if (sticky) { sticky.hidden = true; clear(sticky); }

    var vehicle = usableVehicle(apiData, state.vin);
    if (vehicle) {
      renderVehicleCard(vehicle, state.vin);
    }

    // The checker tells us whether the search actually completed. A failed
    // search must never be reported as "your VIN is clean" — that is the exact
    // claim that got the ads account flagged once already.
    // Missing field = an older cached response = treat as OK.
    var searchStatus = String(apiData.search_status || apiData.searchStatus || 'OK').toUpperCase();

    // FAILED with sites found is not "clean", but it is not nothing either:
    // show what we did find and say plainly that the list may be short. Only
    // a FAILED with an empty list replaces the results entirely.
    if (items.length && searchStatus === 'FAILED') {
      var warnBox = el('div', 'cvf-notice cvf-notice--warn');
      warnBox.appendChild(document.createTextNode(t('failed_partial')));
      results.appendChild(warnBox);
      track('vin_check_error', { error_type: 'search_failed_partial', lang: LANG });
    } else if (items.length && searchStatus === 'PARTIAL') {
      var partBox = el('div', 'cvf-notice');
      partBox.appendChild(document.createTextNode(t('partial_notice')));
      results.appendChild(partBox);
    }

    // PARTIAL with nothing found is the same claim as OK with nothing found —
    // "we looked and found nothing" — except we did not finish looking.
    if (!items.length && (searchStatus === 'FAILED' || searchStatus === 'PARTIAL')) {
      var failed = el('div', 'cvf-error');
      failed.appendChild(el('p', null, t('err_search_failed')));
      var again = el('button', null, t('retry'));
      again.type = 'button';
      again.addEventListener('click', function () { onSubmit(null); });
      failed.appendChild(again);
      results.appendChild(failed);
      appendHelp(results);
      scrollToResults();
      track('vin_check_error', { error_type: 'search_failed', lang: LANG });
      return;
    }

    if (!items.length) {
      var empty = el('div', 'cvf-empty');
      empty.appendChild(el('p', 'cvf-empty__title', t('none_title')));
      empty.appendChild(el('p', null, t('none_body')));
      results.appendChild(empty);
      // Nothing found is exactly when a customer who has seen the page with
      // their own eyes needs a way in.
      appendAddSites(results);
      appendHelp(results);
      scrollToResults();
      return;
    }

    reportPriceDrift(items);
    state.mutedReasons = uninformativeReasons(items);

    var sellable = [], done = [], held = [];
    for (var i = 0; i < items.length; i++) {
      // Held lines leave `sellable` entirely: they must not be counted into a
      // scope tier the customer cannot actually buy.
      if (items[i].inCart) held.push(items[i]);
      else if (items[i].done || items[i].pending) done.push(items[i]);
      else sellable.push(items[i]);
    }

    renderSummary(items, sellable);
    appendTrust(results);

    var tiers = scopeTiers(sellable);
    var recommended = tiers.recommended;
    var rest = sellable.slice(recommended.length);

    renderChoices(tiers, sellable);

    if (recommended.length) {
      var recGroup = el('div', 'cvf-group');
      // With nothing in the "other sites" group there is no second group to
      // distinguish this one from, so the heading has no job. An empty <h3>
      // is a heading with no accessible name plus a stray margin.
      if (rest.length) recGroup.appendChild(el('h3', 'cvf-group__title', t('recommended')));
      recGroup.appendChild(renderList(recommended, true));
      results.appendChild(recGroup);
    }

    /* The rest used to be a closed <details> whose only content was a count.
     * Nobody opens a box labelled "Other sites (15)" straight after being told
     * the recommendation costs $284.98 — the decision is already made by then,
     * and a bare number is not a reason to reopen it.
     *
     * So the first few are always visible, by name, with their price and their
     * own checkbox. Fifteen abstract "others" become three named pages plus a
     * count, and the count now has something attached to it. Everything below
     * still folds away, so the page does not turn into a wall. */
    if (rest.length) {
      var more = el('div', 'cvf-more');

      var head = el('div', 'cvf-more__head');
      head.appendChild(el('span', 'cvf-more__title', t('other_sites', { n: siteCount(rest) })));
      head.appendChild(el('span', 'cvf-more__sum', money(totalOf(rest))));
      more.appendChild(head);

      var preview = rest.slice(0, MORE_PREVIEW);
      var folded = rest.slice(MORE_PREVIEW);
      more.appendChild(renderList(preview, false));

      if (folded.length) {
        var details = el('details', 'cvf-more__rest');
        // The count alone is an affordance, not a reason. The money behind the
        // fold is the reason.
        details.appendChild(el('summary', null,
          t('show_more', { n: siteCount(folded), price: money(totalOf(folded)) })));
        details.appendChild(renderList(folded, false));
        more.appendChild(details);
      }

      results.appendChild(more);
    }

    if (held.length) {
      var heldGroup = el('div', 'cvf-group');
      heldGroup.appendChild(el('h3', 'cvf-group__title', t('in_cart_group')));
      heldGroup.appendChild(renderList(held, false));
      results.appendChild(heldGroup);
    }

    if (done.length) {
      var doneGroup = el('div', 'cvf-group');
      doneGroup.appendChild(el('h3', 'cvf-group__title', t('already_handled')));
      doneGroup.appendChild(renderList(done, false));
      results.appendChild(doneGroup);
    }

    appendAddSites(results);

    /* The contact block used to appear only when something had gone wrong.
     * That is backwards: the customer staring at a nine-row list deciding how
     * to spend $500 is the one most likely to want a person, and they are the
     * one we most want to reach. It sits after the list, so it interrupts
     * nothing, and it is the same block the error states use — one place in
     * the theme editor holds the numbers. */
    appendHelp(results);

    renderSticky();
    updateSticky();
    scrollToResults();

    track('view_item_list', {
      item_list_name: 'vin_check_results',
      items_count: items.length,
      value: totalOf(items) / 100,
      currency: activeCurrency()
    });
  }

  function hasReportOffer(apiData) {
    // If cleaned.vin returned vehicle data for this VIN, its public report page exists,
    // so the partner link is safe to show. Previously this depended on a removal product
    // for cleaned.vin being in the results, which meant the link almost never appeared.
    if (usableVehicle(apiData, state.vin)) return true;
    if (apiData.clean_vin_offer) return true;
    for (var i = 0; i < state.handles.length; i++) {
      if (REPORT_HANDLES.indexOf(state.handles[i]) !== -1) return true;
    }
    return false;
  }

  function renderSummary(items, sellable) {
    var box = el('div', 'cvf-summary');

    var left = el('div');
    left.appendChild(el('h2', 'cvf-summary__title',
      items.length === 1 ? t('result_title_one') : t('result_title', { n: items.length })));
    left.appendChild(el('div', 'cvf-summary__meta', state.vin));
    box.appendChild(left);

    if (sellable.length > 1) {
      var right = el('div', 'cvf-summary__anchor');
      right.appendChild(document.createTextNode(t('full_cleanup') + ': '));
      right.appendChild(el('strong', null, money(totalOf(sellable))));
      box.appendChild(right);
    }

    results.appendChild(box);
  }

  /* How many rows arrive pre-ticked. See the note on recShare in DEFAULTS. */
  function recommendCount(n) {
    var share = Number(CFG.recShare); if (!(share > 0)) share = 60;
    var floor = Number(CFG.recFloor); if (!(floor > 0)) floor = 5;
    var cap = Number(CFG.recCap); if (!(cap > 0)) cap = 10;
    if (cap < floor) cap = floor;

    var want = Math.ceil(n * share / 100);
    if (want < floor) want = floor;
    if (want > cap) want = cap;
    if (want > n) want = n;
    return want;
  }

  /* Two anchors became three.
   *
   * With only "the main ones" and "everything" on offer the gap was 4x, and a
   * cheap option next to a large one is taken almost every time — which is what
   * the orders show. The middle tier is the pre-ticked default. It is not a
   * manufactured option: it is the recommended set, priced as one click.
   *
   * Tiers collapse when they would say the same thing. A middle one site bigger
   * than the core is noise, so below TIER_MIN_GAP the core is dropped and the
   * familiar two-option layout is what renders. */
  var TIER_MIN_GAP = 2;

  // How many of the non-recommended sites are shown by name before the fold.
  var MORE_PREVIEW = 3;

  function scopeTiers(sellable) {
    var n = sellable.length;
    var recN = recommendCount(n);

    var coreN = Number(CFG.recFloor);
    if (!(coreN > 0)) coreN = 5;
    if (coreN > n) coreN = n;

    var out = { core: null, recommended: sellable.slice(0, recN), full: sellable };
    if (recN - coreN >= TIER_MIN_GAP) out.core = sellable.slice(0, coreN);
    return out;
  }

  /* "Full clean-up: $514.97" was a number the customer could read and not act
   * on — to actually buy it they had to tick nine boxes by hand. These are the
   * same figures turned into one click each, with the delivery window attached
   * so the bigger option is a decision and not a leap. */
  function renderChoices(tiers, sellable) {
    var recommended = tiers.recommended;
    if (sellable.length < 2 || recommended.length >= sellable.length) return;

    var wrap = el('div', 'cvf-choice');
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', t('choose_scope'));

    function option(kind, list, titleText) {
      /* The middle tier is the one we want read first, so it carries a badge
         and a heavier card. `hero` is kept on the element as a data attribute
         as well as a class, because markScope() rewrites className wholesale
         and would otherwise strip the styling the moment anything is picked. */
      var hero = kind === 'recommended';
      var btn = el('button', 'cvf-choice__opt' + (hero ? ' cvf-choice__opt--hero' : ''));
      btn.type = 'button';
      btn.setAttribute('data-scope', kind);
      if (hero) btn.setAttribute('data-hero', '1');

      if (hero) btn.appendChild(el('span', 'cvf-choice__badge', t('most_chosen')));
      btn.appendChild(el('span', 'cvf-choice__title', titleText));
      btn.appendChild(el('span', 'cvf-choice__price', money(totalOf(list))));

      var bits = [t('n_sites', { n: list.length })];
      var span = etaSpan(list);
      if (span) bits.push(span);
      btn.appendChild(el('span', 'cvf-choice__meta', bits.join(' · ')));

      // What each option leaves behind, stated plainly. This is the honest
      // version of an upsell: no claim about what removal achieves, just the
      // count of pages that stay up if you pick this one.
      var left = sellable.length - list.length;
      if (kind === 'full') {
        var saved = totalOf(sellable) - totalOf(recommended);
        if (saved > 0 && sellable.length > recommended.length) {
          btn.appendChild(el('span', 'cvf-choice__note', t('full_note', { n: sellable.length - recommended.length })));
        }
      } else if (left > 0) {
        btn.appendChild(el('span', 'cvf-choice__note cvf-choice__note--left', t('leaves_note', { n: left })));
      }

      btn.addEventListener('click', function () {
        applyScope(list);
        track('select_scope', { scope: kind, items_count: list.length, value: totalOf(list) / 100, currency: activeCurrency() });
      });
      return btn;
    }

    var scopes = {};
    if (tiers.core) {
      wrap.appendChild(option('core', tiers.core, t('scope_core')));
      scopes.core = tiers.core;
    }
    wrap.appendChild(option('recommended', recommended, t('scope_recommended')));
    wrap.appendChild(option('full', sellable, t('scope_full')));
    scopes.recommended = recommended;
    scopes.full = sellable;

    results.appendChild(wrap);
    state.scopes = scopes;

    // Removal at the source and Google catching up are two different clocks.
    // Putting the second one right under the first stops the delivery window
    // from reading as a promise about search results.
    cloneBlock('vin-google-note', results, 'cvf-afternote');
  }

  function applyScope(list) {
    var wanted = {};
    for (var i = 0; i < list.length; i++) wanted[list[i].handle] = true;
    for (var j = 0; j < state.items.length; j++) {
      var it = state.items[j];
      if (!it.selectable) continue;
      it.selected = !!wanted[it.handle];
      var cb = rowChecks[it.handle];
      if (cb) cb.checked = it.selected;
    }
    updateSticky();
  }

  // Which of the offered scopes the current ticks correspond to, if any.
  function activeScope() {
    if (!state.scopes) return '';
    var picked = selectedItems();
    var keys = ['core', 'recommended', 'full'], k, i;
    for (k = 0; k < keys.length; k++) {
      var list = state.scopes[keys[k]];
      if (!list) continue;
      if (list.length !== picked.length) continue;
      var set = {}, same = true;
      for (i = 0; i < list.length; i++) set[list[i].handle] = true;
      for (i = 0; i < picked.length; i++) if (!set[picked[i].handle]) { same = false; break; }
      if (same) return keys[k];
    }
    return '';
  }

  function markScope() {
    var active = activeScope();
    var opts = results.querySelectorAll ? results.querySelectorAll('.cvf-choice__opt') : [];
    for (var i = 0; i < opts.length; i++) {
      var on = opts[i].getAttribute('data-scope') === active;
      var hero = opts[i].getAttribute('data-hero') === '1';
      opts[i].className = 'cvf-choice__opt'
        + (hero ? ' cvf-choice__opt--hero' : '')
        + (on ? ' is-active' : '');
      opts[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  function renderList(items, preselect) {
    var list = el('ul', 'cvf-list');
    for (var i = 0; i < items.length; i++) {
      list.appendChild(renderRow(items[i], preselect));
    }
    return list;
  }

  function renderRow(item, preselect) {
    var row = el('li', 'cvf-row');
    if (item.done || item.pending || item.inCart) row.className += ' cvf-row--done';
    if (item.missing || !item.available) row.className += ' cvf-row--unavailable';

    // checkbox
    if (item.selectable) {
      var cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.className = 'cvf-row__check';
      cb.checked = !!preselect;
      cb.setAttribute('data-handle', item.handle);
      cb.setAttribute('aria-label', item.name);
      rowChecks[item.handle] = cb;
      cb.addEventListener('change', function () {
        item.selected = this.checked;
        updateSticky();
        if (this.checked) {
          track('select_item', { item_id: item.handle, price: item.price / 100, currency: activeCurrency() });
        }
      });
      item.selected = !!preselect;
      row.appendChild(cb);
    } else {
      row.appendChild(el('span', 'cvf-row__check'));
      item.selected = false;
    }

    // logo
    if (item.image) {
      var img = document.createElement('img');
      img.className = 'cvf-row__logo';
      img.src = item.image;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 56;
      img.height = 40;
      row.appendChild(img);
    } else {
      row.appendChild(el('span', 'cvf-row__logo'));
    }

    // name + tags
    var body = el('div', 'cvf-row__body');
    var name = el('p', 'cvf-row__name');
    if (item.url) {
      var a = el('a', null, item.name);
      a.href = item.url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      name.appendChild(a);
    } else {
      name.appendChild(document.createTextNode(item.name));
    }
    body.appendChild(name);

    var tags = el('div', 'cvf-row__reasons');
    var added = false;

    if (item.done) {
      var when = formatDate(item.status.verified_at || item.status.removed_at || item.status.ordered_at);
      tags.appendChild(el('span', 'cvf-tag cvf-tag--done',
        item.status.state === 'verified' ? t('status_verified', { date: when }) : t('status_removed', { date: when })));
      added = true;
    } else if (item.pending) {
      tags.appendChild(el('span', 'cvf-tag cvf-tag--progress', t('status_progress')));
      added = true;
    } else if (item.inCart) {
      tags.appendChild(el('span', 'cvf-tag cvf-tag--incart', t('tag_in_cart')));
      added = true;
    } else if (item.missing || !item.available) {
      tags.appendChild(el('span', 'cvf-tag cvf-tag--time', t('status_unavailable')));
      added = true;
    } else {
      var muted = state.mutedReasons || {};
      var shown = {};
      for (var r = 0; r < item.reasons.length; r++) {
        var label = reasonLabel(item.reasons[r]);
        if (!label || muted[label] || shown[label]) continue;
        shown[label] = true;
        tags.appendChild(el('span', 'cvf-tag', label));
        added = true;
      }
      // Same tags, derived straight from the match when the API sends no reasons.
      if (!item.reasons.length) {
        if (typeof item.serpRank === 'number' && item.serpRank <= 3 && !muted[t('tag_serp')]) {
          tags.appendChild(el('span', 'cvf-tag', t('tag_serp'))); added = true;
        }
        if (item.hasPhotos && !muted[t('tag_photos')]) {
          tags.appendChild(el('span', 'cvf-tag', t('tag_photos'))); added = true;
        }
      }
      // No "most common first step" chip here: it fired on every row of the
      // recommended group, and a label that is on everything labels nothing.
      // The group heading already says it, once. The space goes to the
      // delivery window instead, which is the question customers actually ask.
      var etaText = etaLabel(item.etaInfo);
      if (etaText) {
        tags.appendChild(el('span', 'cvf-tag cvf-tag--eta', etaText));
        added = true;
      }
      if (item.isBundle && item.saving > 0) {
        tags.appendChild(el('span', 'cvf-tag cvf-tag--save', t('bundle_saving', { amount: money(item.saving) })));
        added = true;
      }
    }
    if (added) body.appendChild(tags);

    if (item.isBundle && item.covers && item.covers.length) {
      body.appendChild(el('p', 'cvf-row__covers', t('bundle_covers', { sites: item.covers.join(' + ') })));
    }

    if (item.urls && item.urls.length) {
      var found = el('p', 'cvf-row__url');
      found.appendChild(el('span', 'cvf-row__url-label', t('found_at') + ': '));
      var link = el('a', null, shortUrl(item.urls[0]));
      link.href = item.urls[0];
      link.target = '_blank';
      link.rel = 'nofollow noopener noreferrer';
      link.title = item.urls[0];
      if (item.done) {
        link.className = 'is-struck';
        link.title = item.urls[0] + ' — ' + t('page_removed');
        link.setAttribute('aria-label', t('page_removed'));
      }
      found.appendChild(link);
      if (item.urls.length > 1) {
        found.appendChild(el('span', 'cvf-row__url-more', ' ' + t('more_pages', { n: item.urls.length - 1 })));
      }
      body.appendChild(found);
    }

    row.appendChild(body);

    // price
    var price = el('div', 'cvf-row__price');
    if (item.missing || !item.available) {
      price.appendChild(document.createTextNode('—'));
    } else {
      // A bundle's struck-through price is the real sum of what the sites cost
      // separately, not a permanent compare-at — so it is shown even when the
      // compare-at display is off.
      if (item.isBundle && item.separatePrice > item.price) {
        price.appendChild(el('s', null, money(item.separatePrice)));
      } else if (CFG.showCompareAt && item.compareAt && item.compareAt > item.price) {
        price.appendChild(el('s', null, money(item.compareAt)));
      }
      price.appendChild(document.createTextNode(money(item.price)));
    }
    row.appendChild(price);

    return row;
  }

  /* A badge that is on every row is not a badge.
   *
   * Live example, 31.07: the checker returned reasons ["serp_rank_1",
   * "has_photos"] for all nine matched sites, so every row read "First page of
   * Google · Auction photos". True, and useless — nine identical pairs rank
   * nothing and push the delivery window, which does differ, out of the eye.
   * The ordering already carries the ranking; badges should only appear where
   * they separate one row from another.
   *
   * Kept above three rows, where repetition is not yet visual noise. */
  function uninformativeReasons(items) {
    var live = [];
    for (var i = 0; i < items.length; i++) {
      if (!items[i].done && !items[i].pending && !items[i].missing) live.push(items[i]);
    }
    if (live.length < 4) return {};

    // Count the LABEL the customer would read, not the key behind it:
    // `serp_rank_1` and `serp_top` both render "First page of Google", so
    // counting keys would let a badge that is visually on every row survive
    // just because the API used two names for it.
    var counts = {}, label, j, k;
    for (j = 0; j < live.length; j++) {
      var seen = {};
      var reasons = live[j].reasons || [];
      for (k = 0; k < reasons.length; k++) {
        label = reasonLabel(reasons[k]);
        if (!label || seen[label]) continue;      // one row cannot vote twice
        seen[label] = true;
        counts[label] = (counts[label] || 0) + 1;
      }
      if (!reasons.length) {
        if (typeof live[j].serpRank === 'number' && live[j].serpRank <= 3) {
          label = t('tag_serp');
          if (!seen[label]) { seen[label] = true; counts[label] = (counts[label] || 0) + 1; }
        }
        if (live[j].hasPhotos) {
          label = t('tag_photos');
          if (!seen[label]) { seen[label] = true; counts[label] = (counts[label] || 0) + 1; }
        }
      }
    }

    var muted = {};
    for (label in counts) {
      if (Object.prototype.hasOwnProperty.call(counts, label) && counts[label] === live.length) muted[label] = true;
    }
    return muted;
  }

  function reasonLabel(reason) {
    var map = {
      serp_rank_1: t('tag_serp'),
      serp_top: t('tag_serp'),
      has_photos: t('tag_photos'),
      fast_delivery: t('tag_fast')
    };
    return map[reason] || '';
  }

  function formatDate(value) {
    if (!value) return '';
    var d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    // Page language, not the visitor's OS: "12.04.2021" next to "$12,500" is ambiguous
    // (4 December or 12 April?) and reads as a bug.
    try { return d.toLocaleDateString(document.documentElement.lang || 'en'); }
    catch (e) { return d.toLocaleDateString('en'); }
  }

  /* ---------------------------------------------------------------
   * Sticky selection bar + cart
   * ------------------------------------------------------------- */

  function renderSticky() {
    if (!sticky) return;
    clear(sticky);

    var info = el('div', 'vin-sticky__info');
    var label = el('span', 'vin-sticky__label', t('selected_none'));
    label.id = 'vin-sticky-label';
    var eta = el('span', 'vin-sticky__eta');
    eta.id = 'vin-sticky-eta';
    eta.hidden = true;
    info.appendChild(label);
    info.appendChild(eta);

    var btn = el('button', 'vin-sticky__button', t('add_to_cart'));
    btn.type = 'button';
    btn.id = 'vin-sticky-button';
    btn.addEventListener('click', addSelected);

    sticky.appendChild(info);
    sticky.appendChild(btn);
    sticky.hidden = false;
  }

  function selectedItems() {
    var out = [];
    for (var i = 0; i < state.items.length; i++) {
      if (state.items[i].selected && state.items[i].selectable) out.push(state.items[i]);
    }
    return out;
  }

  function updateSticky() {
    var label = $('vin-sticky-label');
    var btn = $('vin-sticky-button');
    if (!label || !btn) return;

    var picked = selectedItems();
    var sum = 0;
    for (var i = 0; i < picked.length; i++) sum += picked[i].price;

    clear(label);
    label.appendChild(document.createTextNode(
      picked.length ? t('selected', { n: picked.length }) : t('selected_none')
    ));

    clear(btn);
    btn.appendChild(document.createTextNode(
      picked.length ? t('add_n_to_cart', { n: picked.length, price: money(sum) }) : t('add_to_cart')
    ));
    btn.disabled = picked.length === 0;

    var span = etaSpan(picked);
    var etaNode = $('vin-sticky-eta');
    if (etaNode) {
      clear(etaNode);
      if (span) etaNode.appendChild(document.createTextNode(span));
      etaNode.hidden = !span;
    }

    markScope();
  }

  // The customer reads this value on the order and in the confirmation email:
  // it is the answer to "what exactly am I paying to have taken down?".
  // A URL cut in half answers nothing and looks like a bug, so we only ever
  // add whole addresses and say plainly how many did not fit. The authoritative
  // full list stays with the checker, keyed by `vin` + `_invoice`.
  var URL_PROP_MAX = 255;

  function urlProperty(urls) {
    if (!urls || !urls.length) return '';
    var kept = [];
    var used = 0;
    for (var i = 0; i < urls.length; i++) {
      var sep = kept.length ? 3 : 0;                       // ' | '
      var left = urls.length - i - 1;                      // still unplaced
      var marker = left > 0 ? (' | +' + left + ' more').length : 0;
      if (used + sep + urls[i].length + marker > URL_PROP_MAX) break;
      used += sep + urls[i].length;
      kept.push(urls[i]);
    }
    if (!kept.length) return urls[0].slice(0, URL_PROP_MAX);  // one absurd URL
    var dropped = urls.length - kept.length;
    // Same separator as between URLs, so the marker reads as its own item
    // rather than as a tail glued onto the last address.
    if (dropped > 0) kept.push('+' + dropped + ' more');
    return kept.join(' | ');
  }

  /* What the customer is already holding. Read once per page, tolerant of
   * failure: not knowing the cart must never stop the results rendering. */
  function cartVariants() {
    if (state.cartPromise) return state.cartPromise;
    state.cartPromise = fetchWithTimeout(routesRoot() + 'cart.js', {
      headers: { 'Accept': 'application/json' }
    }, 8000)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        /* variantId -> { VIN: true }.
         *
         * Keyed by VIN and not by variant alone, because the same product sold
         * for a different car is a different job and a sale we should still be
         * making. A customer who removed atlanticexpress for one VIN must be
         * able to buy atlanticexpress for the next one; only the same product
         * for the SAME VIN is a duplicate.
         *
         * Lines with no `vin` property were not added by this widget, so they
         * cannot be attributed to any VIN and never block anything. */
        var map = {};
        if (data && isArray(data.items)) {
          for (var i = 0; i < data.items.length; i++) {
            var line = data.items[i];
            var id = line.variant_id || line.id;
            if (!id) continue;
            var props = line.properties || {};
            var lineVin = props.vin ? String(props.vin).toUpperCase() : '';
            if (!lineVin) continue;
            if (!map[String(id)]) map[String(id)] = {};
            map[String(id)][lineVin] = true;
          }
        }
        state.cartVariants = map;
        return map;
      })['catch'](function (e) {
        warn('cart read failed', e);
        state.cartVariants = {};
        return {};
      });
    return state.cartPromise;
  }

  function markAlreadyInCart(items) {
    var map = state.cartVariants || {};
    var vin = String(state.vin || '').toUpperCase();
    if (!vin) return;
    state.inCart = state.inCart || {};
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!it.variantId) continue;
      var vins = map[String(it.variantId)];
      if (!vins || !vins[vin]) continue;
      it.inCart = true;
      it.selectable = false;
      state.inCart[it.handle] = true;
    }
  }

  // A bundle line is one product but covers several sites. Everything that
  // counts sites rather than line items has to go through this.
  function siteSpan(item) {
    return (item.isBundle && item.covers && item.covers.length) ? item.covers.length : 1;
  }

  // Pages, not rows. A bundle is one row covering several sites, and the
  // customer counts pages — so every number phrased as "pages" goes through
  // this and never through list.length.
  function siteCount(list) {
    var n = 0;
    for (var i = 0; i < list.length; i++) n += siteSpan(list[i]);
    return n;
  }

  /* Immediate, visible proof that the click did something.
   *
   * Without this the page looked identical after "Add 10 to cart": same ticked
   * boxes, same sticky total, plus a panel mentioning another ten. Two tens on
   * one screen with nothing marking which was which. Now the rows that went in
   * say so, and the only live number left is the one still for sale. */
  function markRowsAdded(list) {
    for (var i = 0; i < list.length; i++) {
      var it = list[i];
      it.inCart = true;
      it.selected = false;
      it.selectable = false;

      var cb = rowChecks[it.handle];
      if (!cb || !cb.parentNode) continue;
      var row = cb.parentNode;                       // the checkbox is a direct child
      if (row.className.indexOf('cvf-row--done') === -1) row.className += ' cvf-row--done';

      var tags = row.querySelector ? row.querySelector('.cvf-row__reasons') : null;
      if (tags) {
        var tag = el('span', 'cvf-tag cvf-tag--incart', t('tag_in_cart'));
        tags.insertBefore(tag, tags.firstChild);
      }
      row.replaceChild(el('span', 'cvf-row__check'), cb);
      rowChecks[it.handle] = null;
    }
  }

  function cartCoverage() {
    var inCart = state.inCart || {}, held = 0, open = 0, openCents = 0;
    for (var i = 0; i < state.items.length; i++) {
      var it = state.items[i];
      if (inCart[it.handle]) { held += siteSpan(it); continue; }
      if (!it.selectable) continue;
      open += siteSpan(it);
      openCents += it.price;
    }
    return { held: held, open: open, openCents: openCents };
  }

  // The size of the find, as stamped when the results were built.
  function foundCount() {
    return (typeof state.foundCount === 'number') ? state.foundCount : state.items.length;
  }
  function foundCents() {
    return (typeof state.foundCents === 'number') ? state.foundCents : totalOf(state.items);
  }

  // A bundle line stands for several sites, so it carries several ids.
  function siteIdsFor(item) {
    var handles = (item.isBundle && item.coverHandles && item.coverHandles.length)
      ? item.coverHandles : [item.handle];
    var out = [];
    for (var i = 0; i < handles.length; i++) {
      var h = handles[i];
      if (!h) continue;
      out.push(siteIdByHandle[h] || h);
    }
    return out.join(',');
  }

  /* Everything the checker needs to reconcile this order, written as line-item
   * properties in the same request that adds the product.
   *
   * Not as cart attributes. Shopify's express buttons — PayPal Express, Apple
   * Pay, Google Pay, Shop Pay — build the checkout straight from the line items
   * and drop cart attributes and the cart note on the way. Measured over the 11
   * checker orders between 5 and 12 August 2026: four arrived with no
   * `checker_*` at all (#20940, #20935, #20924, #20919) and in every one of the
   * four the line properties came through intact.
   *
   * Only facts that cannot go stale live here. "How many are still open" is
   * deliberately absent: it changes every time the customer edits the cart, so
   * a value captured at add-time would start lying the moment they removed a
   * line. The cart page and the email derive it as `_checker_found` minus what
   * the cart actually holds — right by construction, and self-correcting. */
  /* Nazwy stron, które po tym dodaniu do koszyka zostają nietknięte.
   *
   * Mail po realizacji potrafi policzyć ILE stron zostało (`_checker_found`
   * minus suma `_checker_span`), ale nie wie, KTÓRE. Liczba sama w sobie nie
   * sprzedaje — „zostały 3 strony" brzmi jak zaokrąglenie, „bidfax.info,
   * stat.vin i bid.cars" brzmi jak fakt.
   *
   * Zapisujemy to tutaj, a nie po stronie checkera, bo widget w tym momencie
   * i tak zna całą listę. Zero pracy dla dewelopera i zero dodatkowych żądań.
   *
   * Wartość to migawka z chwili dodania. Przy drugim dodaniu (upsell) nowe
   * pozycje dostają nowszą, KRÓTSZĄ listę — koszyk w międzyczasie urósł. Mail
   * bierze najkrótszą z pozycji zamówienia, bo kolejność pozycji na zamówieniu
   * nie jest gwarantowana, a krótsza z definicji znaczy nowsza.
   */
  var OPEN_NAMES_MAX = 12;

  /* A property value is stored on the order forever and read by an e-mail
   * template, not by code we control end to end. Long values have a habit of
   * being silently truncated somewhere in that chain, and a half-written row
   * would print as a site with no price. Cheaper to stop early than to find
   * out on a customer's order. */
  var OPEN_NAMES_BUDGET = 420;

  function openNamesAfter(list) {
    var pending = {}, i;
    for (i = 0; i < list.length; i++) pending[list[i].handle] = true;

    var inCart = state.inCart || {};
    var rows = [], extra = 0, used = 0;
    for (i = 0; i < state.items.length; i++) {
      var it = state.items[i];
      if (pending[it.handle] || inCart[it.handle]) continue;
      if (!it.selectable) continue;

      /* "Bid.cars~6000" — name and price in cents.
       *
       * The e-mail lays this out as a table, and it cannot ask the shop what
       * a row costs: a notification template has no access to any product but
       * the ones on its own order. So the price travels with the name.
       *
       * Comma separates rows and tilde separates the fields, so both are
       * stripped out of the name. A merchant-renamed site is the only way one
       * could turn up there, but that is one rename away at any time. */
      var name = String(it.name || '').replace(/[~,]+/g, ' ');
      var row = name + '~' + (it.price || 0);

      if (rows.length < OPEN_NAMES_MAX && used + row.length + 2 <= OPEN_NAMES_BUDGET) {
        rows.push(row);
        used += row.length + 2;
      } else {
        extra++;
      }
    }
    if (!rows.length) return '';
    // No tilde: that is how the e-mail tells the overflow row from a site.
    if (extra) rows.push('+' + extra);
    return rows.join(', ');
  }

  // Rows picked by hand in the "site not listed" modal are not part of what the
  // scan found. The fulfilled-order email subtracts what was bought from the
  // size of the find, so those rows have to be tellable apart or the arithmetic
  // goes negative. Membership in `state.items` is the test: it holds on every
  // call path, unlike a flag passed down from whichever handler ran.
  function inFind(handle) {
    var list = state.items || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle) return true;
    }
    return false;
  }

  function cartPayload(list) {
    var payload = { items: [] };
    var found = String(foundCount());
    var cents = String(foundCents());
    var openNames = openNamesAfter(list);

    for (var i = 0; i < list.length; i++) {
      var it = list[i];

      // `vin` and `url` are shown to the customer on the order and in the
      // confirmation email — that is the point: they see exactly which page
      // they are paying to have removed. Everything with a leading underscore
      // is ours and stays hidden from the customer everywhere.
      var props = { vin: state.vin };
      var urlProp = urlProperty(it.urls);
      if (urlProp) props.url = urlProp;
      if (state.invoice) props._invoice = state.invoice;

      var sid = siteIdsFor(it);
      if (sid) props._checker_site_id = sid;
      props._checker_span = String(siteSpan(it));
      props._checker_found = found;
      props._checker_found_cents = cents;
      if (openNames) props._checker_open = openNames;
      if (!inFind(it.handle)) props._checker_extra = '1';

      payload.items.push({ id: it.variantId, quantity: 1, properties: props });
    }
    return payload;
  }

  function markInCart(list) {
    if (!state.inCart) state.inCart = {};
    for (var i = 0; i < list.length; i++) state.inCart[list[i].handle] = true;
  }

  // Sellable rows this session has not already pushed into the cart.
  function notInCart() {
    var out = [], inCart = state.inCart || {};
    for (var i = 0; i < state.items.length; i++) {
      var it = state.items[i];
      if (!it.selectable || inCart[it.handle]) continue;
      out.push(it);
    }
    return out;
  }

  function postToCart(list) {
    return fetchWithTimeout(routesRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(cartPayload(list))
    }, 20000)
      .then(function (r) {
        if (!r.ok) throw new Error('cart_http_' + r.status);
        return r.json();
      })
      .then(function (data) {
        markInCart(list);
        return data;
      });
  }

  function goToCart() {
    window.location.href = routesRoot() + 'cart';
  }

  /* Arriving from the e-mail with the cart already decided.
   *
   * The e-mail listed the pages and the price, so making the customer tick
   * the same boxes again is one step too many. This fills the cart and hands
   * them straight to /cart, where the discount from /discount/NEXT5 is
   * already sitting and every line can still be removed.
   *
   * Only what THIS scan found goes in. If two of the four pages came down in
   * the meantime, the customer pays for two — the e-mail's promise is not
   * allowed to outrank what is on screen now.
   *
   * A failure here is not fatal: the notice comes off and the normal list is
   * still there to tick by hand.
   */
  function autoCartFromLink() {
    if (!state.autoCart || state.autoCartDone) return;
    state.autoCartDone = true;

    var list = notInCart();
    if (!list.length) {
      var done = el('div', 'cvf-notice cvf-notice--added');
      done.appendChild(el('span', null, t('auto_cart_none')));
      results.insertBefore(done, results.firstChild);
      track('auto_cart_empty', { lang: LANG });
      return;
    }

    var note = el('div', 'cvf-notice cvf-notice--added');
    note.appendChild(el('span', null, t('auto_cart_adding', { n: siteCount(list) })));
    results.insertBefore(note, results.firstChild);
    try { if (note.scrollIntoView) note.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    catch (e) {}

    track('add_to_cart', {
      value: totalOf(list) / 100,
      currency: activeCurrency(),
      items_count: list.length,
      source: 'email_link'
    });

    postToCart(list)
      .then(stampCart)
      .then(goToCart)
      ['catch'](function (e) {
        warn('auto cart failed', e);
        if (note.parentNode) note.parentNode.removeChild(note);
        track('vin_check_error', { error_type: 'auto_cart', lang: LANG });
      });
  }

  // Order-level join key for the checker: survives on the order even when
  // line-item properties get edited or the order is built by support.
  //
  // Also the one moment we learn the real cart total — /cart/update.js answers
  // with the whole cart. That figure matters: the offer below quotes what the
  // cart would become, and a customer who arrived with something already in it
  // deserves the true number, not our own subtotal.
  function stampCart() {
    return setCartAttributes()
      .then(function (r) { return (r && r.json) ? r.json() : null; })
      .then(function (cart) {
        if (cart && typeof cart.total_price === 'number') state.cartTotal = cart.total_price;
        return null;
      })['catch'](function () { return null; });
  }

  function addSelected() {
    var picked = selectedItems();
    if (!picked.length) return;

    var btn = $('vin-sticky-button');
    if (btn) btn.disabled = true;

    // Captured now, on purpose. totalOf only counts rows that are still
    // sellable, and marking these as held — which is the whole point of the
    // next step — takes them out of that sum. Reading the receipt afterwards
    // would print $0.00.
    var pickedTotal = totalOf(picked);

    track('add_to_cart', {
      value: pickedTotal / 100,
      currency: activeCurrency(),
      items_count: picked.length
    });

    postToCart(picked)
      .then(stampCart)
      .then(function () {
        // Mark before offering: the list has to show what just happened before
        // the panel talks about what did not.
        markRowsAdded(picked);
        var left = notInCart();
        if (!left.length) { goToCart(); return; }
        showUpsell(picked, left, pickedTotal);
      })
      ['catch'](function (e) {
        warn('cart add failed', e);
        if (btn) btn.disabled = false;
        var box = el('div', 'cvf-error', t('err_cart'));
        results.appendChild(box);
        track('vin_check_error', { error_type: 'cart', lang: LANG });
      });
  }

  /* The one moment the customer is provably willing to pay.
   *
   * They have just committed; the cart call has already succeeded, so nothing
   * here can cost them the order they already placed. The offer names the pages
   * that stay online rather than asking them to imagine fifteen of something,
   * and "go to cart" is a real button, not a greyed-out afterthought — a
   * customer who wants out gets out in one click.
   *
   * It shows once. A second prompt after a decline is nagging, not selling. */
  function showUpsell(bought, left, boughtTotal) {
    if (state.upsellShown) { goToCart(); return; }
    state.upsellShown = true;

    var sum = totalOf(left);
    var leftPages = siteCount(left);
    var boughtPages = siteCount(bought);

    var panel = el('div', 'cvf-upsell');
    panel.setAttribute('role', 'group');
    panel.setAttribute('aria-label', t('upsell_title', { n: leftPages }));

    // The outcome first. The customer clicked "add to cart" and the honest
    // next word is whether that worked — not a new offer. Leading with the
    // pitch is what made the panel read as a second, competing basket.
    panel.appendChild(el('p', 'cvf-upsell__done',
      t('upsell_added', { n: boughtPages, price: money(boughtTotal) })));

    panel.appendChild(el('p', 'cvf-upsell__title', t('upsell_title', { n: leftPages })));
    panel.appendChild(el('p', 'cvf-upsell__body', t('upsell_body')));

    var names = [], i;
    for (i = 0; i < left.length && i < 4; i++) names.push(left[i].name);
    if (left.length > names.length) names.push(t('upsell_and_more', { n: left.length - names.length }));
    panel.appendChild(el('p', 'cvf-upsell__names', names.join(' · ')));

    /* Say the price twice, on purpose.
     *
     * The button carries the amount, but a button price reads as a label. This
     * line states it as a charge and then names what the cart becomes — the
     * opposite of hiding the increment, which is the usual trick here and the
     * last thing a business selling trust should be doing. */
    var base = (typeof state.cartTotal === 'number') ? state.cartTotal : boughtTotal;
    panel.appendChild(el('p', 'cvf-upsell__cost',
      t('upsell_cost', { price: money(sum), total: money(base + sum) })));

    var actions = el('div', 'cvf-upsell__actions');

    var add = el('button', 'cvf-upsell__add', t('upsell_add', { n: leftPages, price: money(sum) }));
    add.type = 'button';
    var skip = el('button', 'cvf-upsell__skip', t('upsell_skip'));
    skip.type = 'button';

    add.addEventListener('click', function () {
      add.disabled = true;
      skip.disabled = true;
      track('add_to_cart', {
        value: sum / 100, currency: activeCurrency(),
        items_count: left.length, upsell: true
      });
      postToCart(left).then(stampCart).then(goToCart)['catch'](function (e) {
        // The first basket is already in the cart. Sending them there beats
        // stranding them on a dead panel.
        warn('upsell add failed', e);
        goToCart();
      });
    });

    skip.addEventListener('click', function () {
      track('skip_upsell', { items_count: left.length, value: sum / 100, currency: activeCurrency() });
      goToCart();
    });

    actions.appendChild(add);
    actions.appendChild(skip);
    panel.appendChild(actions);
    results.appendChild(panel);

    // Only now — a panel that failed to build must not take the sticky bar
    // with it and leave the page with no way forward.
    if (sticky) sticky.hidden = true;

    track('view_upsell', { items_count: left.length, value: sum / 100, currency: activeCurrency() });

    if (panel.scrollIntoView) panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (add.focus) add.focus();
  }

  function setCartAttributes() {
    var cov = cartCoverage();
    var attrs = {
      checker_invoice: state.invoice || '',
      checker_vin: state.vin,
      checker_sites_found: String(foundCount()),
      checker_total_cents: String(foundCents()),
      // The cart page reads these to offer what was left behind. Counted in
      // sites, not line items, so a bundle counts for everything it covers.
      checker_sites_in_cart: String(cov.held),
      checker_sites_open: String(cov.open),
      checker_open_cents: String(cov.openCents)
    };
    return fetchWithTimeout(routesRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }, 10000);
  }

  /* ---------------------------------------------------------------
   * Vehicle card (kept from the live widget, rebuilt without innerHTML)
   * ------------------------------------------------------------- */

  function renderVehicleCard(vd, vin) {
    var record = vd.primary_record ||
                 (isArray(vd.records) && vd.records.length ? vd.records[0] : null) ||
                 vd.vehicle || vd;

    // cleaned.vin returns imageUrls (array of full URLs); older shapes used photos/images
    var photos = record.imageUrls || record.photos || record.images || record.gallery ||
                 vd.imageUrls || vd.photos || vd.images || [];
    var images = [];
    for (var i = 0; i < photos.length; i++) {
      var url = safeUrl(getPhotoUrl(photos[i]));
      if (!url) continue;
      var low = url.toLowerCase();
      if (low.indexOf('.mp4') !== -1 || low.indexOf('.webm') !== -1 || low.indexOf('.mov') !== -1) continue;
      if (images.indexOf(url) === -1) images.push(url);
    }

    var vinDisplay = record.vin || vd.vin || vin || '';
    var title = record.vehicle_name || record.vehicleName || record.title || '';
    if (!title) {
      var bits = [];
      if (record.year || vd.year) bits.push(record.year || vd.year);
      if (record.make || vd.make) bits.push(record.make || vd.make);
      if (record.model || vd.model) bits.push(record.model || vd.model);
      if (record.trim) bits.push(record.trim);
      title = bits.join(' ');
    }

    var card = el('section', 'vehicle-card');
    card.appendChild(el('p', 'vehicle-card__intro', t('vehicle_intro')));

    var media = el('div', 'vehicle-card__media');
    var mainImg = null;
    var thumbs = [];
    var syncTo = null;   // set below when there is a gallery to keep in step

    if (images.length) {
      var frame = el('div', 'vehicle-main-image');
      var prev = null, next = null;

      if (images.length > 1) {
        prev = el('button', 'vehicle-main-arrow vehicle-main-arrow--prev', '‹');
        prev.type = 'button';
        prev.setAttribute('aria-label', t('photo_prev'));
        frame.appendChild(prev);
      }

      mainImg = document.createElement('img');
      mainImg.className = 'vehicle-main-img';
      mainImg.src = images[0];
      mainImg.alt = title || vinDisplay;
      frame.appendChild(mainImg);

      if (images.length > 1) {
        next = el('button', 'vehicle-main-arrow vehicle-main-arrow--next', '›');
        next.type = 'button';
        next.setAttribute('aria-label', t('photo_next'));
        frame.appendChild(next);
      }
      media.appendChild(frame);

      if (images.length > 1) {
        var gallery = el('div', 'vehicle-gallery');
        var limit = Math.min(images.length, 10);
        for (var j = 0; j < limit; j++) {
          var tb = el('button', 'vehicle-thumb' + (j === 0 ? ' is-active' : ''));
          tb.type = 'button';
          tb.setAttribute('data-index', String(j));
          // The button's only child is a decorative image, so without this it
          // is announced as an unnamed "button" — nine times in a row.
          tb.setAttribute('aria-label', t('photo_n', { n: j + 1 }));
          var ti = document.createElement('img');
          ti.src = images[j];
          ti.alt = '';
          ti.loading = 'lazy';
          tb.appendChild(ti);
          gallery.appendChild(tb);
          thumbs.push(tb);
        }
        media.appendChild(gallery);

        var current = 0;
        var setIndex = function (idx) {
          var total = images.length;
          if (idx < 0) idx = total - 1;
          if (idx >= total) idx = 0;
          current = idx;
          mainImg.src = images[current];
          for (var k = 0; k < thumbs.length; k++) {
            if (k === current) thumbs[k].className = 'vehicle-thumb is-active';
            else thumbs[k].className = 'vehicle-thumb';
          }
        };
        syncTo = setIndex;
        if (prev) prev.addEventListener('click', function () { setIndex(current - 1); });
        if (next) next.addEventListener('click', function () { setIndex(current + 1); });
        for (var m = 0; m < thumbs.length; m++) {
          (function (idx, node) {
            node.addEventListener('click', function () { setIndex(idx); });
          })(m, thumbs[m]);
        }
      }
    }
    if (images.length) {
      attachImageFallbacks(media, mainImg, thumbs, images, syncTo);
      card.appendChild(media);
    } else {
      // A record can be renderable on facts alone (year/make/auction/price)
      // and still carry no photos. Appending an empty media div leaves the
      // 1.2fr track in place and the details squeeze into the right-hand
      // column beside a hole — the same failure the onerror path repairs.
      card.className += ' vehicle-card--nomedia';
    }

    var details = el('div', 'vehicle-card__details');
    details.appendChild(el('h3', 'vehicle-title', title || vinDisplay));

    // Optional one-liner from cleaned.vin. Absent for most records and that is
    // fine — the chips carry the facts. Never invent one on the client.
    var desc = record.description || record.summary || vd.description || '';
    if (desc && String(desc).length > 3) {
      details.appendChild(el('p', 'vehicle-desc', String(desc).slice(0, 300)));
    }

    var meta = el('div', 'vehicle-meta');
    addChip(meta, t('chip_vin'), vinDisplay);
    addChip(meta, t('chip_lot'), record.lot_number || record.lotNumber || record.lot);
    addChip(meta, t('chip_auction'), record.auction_house || record.auctionName || record.auction || record.source);
    addChip(meta, t('chip_location'), record.location || record.auction_location);
    addChip(meta, t('chip_sale_date'), formatDate(record.sale_date || record.saleDate || record.date_of_sale));
    addChip(meta, t('chip_sold_for'), formatPrice(record.purchase_price || record.purchasePrice || record.sale_price || record.final_bid || record.price));
    addChip(meta, t('chip_odometer'), formatOdometer(record.odometer || record.mileage));
    addChip(meta, t('chip_doc'), record.docType || record.document_type, true);
    addChip(meta, t('chip_damage'), record.primary_damage || record.primaryDamage || record.damage, true);
    details.appendChild(meta);


    card.appendChild(details);
    results.appendChild(card);
  }

  /* IAAI usuwa miniatury starszych lotów, więc część imageUrls odpowiada 404.
     Pusta ramka wygląda gorzej niż brak zdjęcia: przechodzimy do następnego
     adresu, a gdy wszystkie padną — chowamy całą kolumnę ze zdjęciami. */
  function attachImageFallbacks(media, mainImg, thumbs, images, syncTo) {
    var dead = {};
    if (mainImg) {
      mainImg.onerror = function () {
        // getAttribute, not .src — the property returns a resolved absolute URL
        // that would not key-match the raw strings in `images`, which would
        // silently unbound this retry loop. Do not "simplify" this.
        dead[mainImg.getAttribute('src')] = true;
        for (var i = 0; i < images.length; i++) {
          if (!dead[images[i]]) {
            // Go through the gallery's own setter when there is one, so the
            // highlighted thumbnail and the arrows' idea of "current" follow
            // the swap. Setting .src directly leaves them pointing at a photo
            // the customer is no longer looking at.
            if (syncTo) syncTo(i); else mainImg.src = images[i];
            return;
          }
        }
        // Collapse the card to one column too, otherwise the details squeeze
        // into the narrow 1.2fr track and leave dead space beside them.
        if (media && media.parentNode) {
          var card = media.parentNode;
          card.className += ' vehicle-card--nomedia';
          card.removeChild(media);
        }
      };
    }
    for (var j = 0; j < thumbs.length; j++) {
      (function (btn) {
        var im = btn.getElementsByTagName('img')[0];
        if (im) im.onerror = function () { btn.style.display = 'none'; };
      })(thumbs[j]);
    }
  }

  function addChip(host, label, value, danger) {
    if (value === undefined || value === null || value === '') return;
    var chip = el('span', 'vehicle-chip' + (danger ? ' vehicle-chip--danger' : ''));
    chip.appendChild(el('strong', null, label + ': '));
    chip.appendChild(document.createTextNode(String(value)));
    host.appendChild(chip);
  }

  // Auction figures are US data: group them the way the page's language does, not the
  // way the visitor's OS happens to be configured, or a US sale price reads as broken.
  function groupNumber(n) {
    try { return n.toLocaleString(document.documentElement.lang || 'en'); }
    catch (e) { return n.toLocaleString('en'); }
  }

  function formatPrice(v) {
    if (v === undefined || v === null || v === '') return '';
    var n = Number(v);
    if (isNaN(n)) return String(v);
    return '$' + groupNumber(n);
  }

  function formatOdometer(v) {
    if (v === undefined || v === null || v === '') return '';
    var n = Number(v);
    if (isNaN(n) || n <= 0) return '';
    return groupNumber(n) + ' ' + t('unit_miles');
  }

  /* ---------------------------------------------------------------
   * Errors
   * ------------------------------------------------------------- */

  /* `kind === 'rate'` is a 429 from the checker, which now limits checks per VIN
   * per hour. Offering "Try again" there is worse than useless: the retry fails
   * by definition, the customer clicks it repeatedly, and the repeat clicks are
   * exactly the pattern the limiter watches for. So that case gets its own
   * message, no retry button, and the human route instead. */
  function renderError(kind) {
    if (slowTimer) window.clearTimeout(slowTimer);
    stopProgress();
    clear(results);
    var box = el('div', 'cvf-error');

    if (kind === 'rate') {
      box.appendChild(document.createTextNode(t('err_rate_limited')));
    } else {
      box.appendChild(document.createTextNode(t('err_api') + ' '));
      var retry = el('button', null, t('retry'));
      retry.type = 'button';
      retry.addEventListener('click', function () { onSubmit(null); });
      box.appendChild(retry);
    }

    results.appendChild(box);
    appendHelp(results);
  }

  /* ------------------------------------------------------------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();